import { db, storage } from '../config/firebase';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  collection, doc, collectionGroup, getCountFromServer,
  getDocs, getDoc,
  addDoc, updateDoc, setDoc, deleteDoc,
  query, orderBy, where, limit, startAfter,
  FieldPath, increment, onSnapshot, documentId, arrayUnion, arrayRemove, serverTimestamp
} from 'firebase/firestore';
import { COLLECTIONS, ROLES, DEFAULT_ROLE_PERMISSIONS, normalizeRole, ADMIN_ACTION_TYPES, ACTION_ITEM_STATUS, OFFICIAL_EMAILS } from '../constants';
import { getLastSundayCutoff } from '../utils/date';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const col  = (...segments) => collection(db, segments.join('/'));
const ref  = (...segments) => doc(db, segments.join('/'));

/** Add a doc and immediately write back its auto-generated ID as a field in a single setDoc operation */
const addWithId = async (collectionRef, data, idField) => {
  const docRef = doc(collectionRef);
  const payload = idField ? { ...data, [idField]: docRef.id } : { ...data };
  await setDoc(docRef, payload);
  return { id: docRef.id, ...payload };
};

/** Delete all docs in a subcollection */
const _deleteSubcollection = async (path) => {
  const snap = await getDocs(collection(db, path));
  await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
};

// ─── Seasons ──────────────────────────────────────────────────────────────────
export const seasonsService = {
  /** Get ALL seasons (Admin) */
  getAll: async () => {
    const snap = await getDocs(col(COLLECTIONS.seasons));
    return snap.docs.map((d) => ({
      id: d.id,
      season_id: d.data().season_id || d.id,
      ...d.data()
    }));
  },

  /** Get one season by ID */
  getOne: async (seasonId) => {
    if (!seasonId) return null;
    const snap = await getDoc(ref(COLLECTIONS.seasons, seasonId));
    return snap.exists() ? { id: snap.id, season_id: snap.data()?.season_id || snap.id, ...snap.data() } : null;
  },

  /** Get seasons where user has Owner, Contributor, or Reader roles using fast array indexing */
  getByHostEmail: async (email) => {
    if (!email) return [];
    const normalizedEmail = email.trim().toLowerCase();
    const map = new Map();

    try {
      // 1. Primary query: array-contains on authorized_emails
      const qArray = query(
        col(COLLECTIONS.seasons),
        where('authorized_emails', 'array-contains', normalizedEmail)
      );
      const snapArray = await getDocs(qArray);
      snapArray.docs.forEach((d) => {
        map.set(d.id, {
          id: d.id,
          season_id: d.data().season_id || d.id,
          ...d.data()
        });
      });
    } catch (err) {
      console.warn('Error querying seasons by authorized_emails:', err);
    }

    // 2. Fallback query for direct host_email
    try {
      const qHost = query(
        col(COLLECTIONS.seasons),
        where('host_email', '==', normalizedEmail)
      );
      const snapHost = await getDocs(qHost);
      snapHost.docs.forEach((d) => {
        if (!map.has(d.id)) {
          map.set(d.id, {
            id: d.id,
            season_id: d.data().season_id || d.id,
            ...d.data()
          });
        }
      });
    } catch {
      // ignore host_email query errors
    }

    // 3. Fallback query for legacy seasons not yet indexed with authorized_emails
    try {
      const qLegacy = query(
        col(COLLECTIONS.seasons),
        where(new FieldPath('access_control', email), 'in', ['Owner', 'Contributor', 'Reader'])
      );
      const snapLegacy = await getDocs(qLegacy);
      snapLegacy.docs.forEach((d) => {
        if (!map.has(d.id)) {
          map.set(d.id, {
            id: d.id,
            season_id: d.data().season_id || d.id,
            ...d.data()
          });
        }
      });
    } catch {
      // ignore legacy field path query errors
    }

    return Array.from(map.values());
  },

  /** Create season, ensures authorized_emails and access_control are initialized */
  create: async (data) => {
    const accessControl = data.access_control || {};
    const hostEmail = data.host_email || data.creator_email || data.user_email || '';
    if (hostEmail && !accessControl[hostEmail]) {
      accessControl[hostEmail] = 'Owner';
    }
    const authorizedEmails = Array.from(new Set([
      ...(data.authorized_emails || []),
      ...Object.keys(accessControl).map((e) => e.trim().toLowerCase())
    ])).filter(Boolean);

    const isSingleContest = data.type === 'Single Contest';
    const seasonPayload = {
      ...data,
      quizzes_count: Number(data.quizzes_count !== undefined ? data.quizzes_count : 0) || 0,
      host_email: hostEmail,
      access_control: accessControl,
      authorized_emails: authorizedEmails,
      auto_update_quiz_status: data.auto_update_quiz_status !== undefined ? data.auto_update_quiz_status : false,
      excludeFromRanking: data.excludeFromRanking !== undefined ? data.excludeFromRanking : true,
      ...(isSingleContest ? { season_leaderboard_available: false } : {}),
    };

    const docRef = await addWithId(col(COLLECTIONS.seasons), seasonPayload, 'season_id');

    // If Single Contest, ensure leaderboardSetting has season_leaderboard_available: false
    if (isSingleContest) {
      try {
        await upsert(COLLECTIONS.leaderboardSetting, docRef.id, {
          season_leaderboard_available: false,
          view: 'single',
          appearance: 'after_user_played',
        });
      } catch (lbErr) {
        console.warn('Failed to initialize leaderboardSetting for single contest:', lbErr);
      }
    }

    // If submitted under review, log an admin action item
    if (data.status === 'under-review') {
      try {
        await addDoc(col(COLLECTIONS.adminActionItems), {
          type: ADMIN_ACTION_TYPES.seasonReview,
          targetId: docRef.id,
          targetDocId: docRef.id,
          title: `New Season Proposal: ${data.season}`,
          message: `Host ${data.host || hostEmail} submitted a new season for onboarding review.`,
          hostName: data.host,
          hostEmail: hostEmail,
          hostPhone: data.host_phone,
          status: ACTION_ITEM_STATUS.open,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } catch (err) {
        console.warn('Failed to log admin action item for season review:', err);
      }
    }

    return docRef;
  },

  /** Update a season document, keeping authorized_emails synced with access_control */
  update: async (seasonId, data) => {
    const payload = { ...data };
    if (payload.access_control) {
      payload.authorized_emails = Object.keys(payload.access_control)
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean);
    }
    if (payload.type === 'Single Contest') {
      payload.season_leaderboard_available = false;
      try {
        await upsert(COLLECTIONS.leaderboardSetting, seasonId, {
          season_leaderboard_available: false,
        });
      } catch (lbErr) {
        console.warn('Failed to update leaderboardSetting for single contest:', lbErr);
      }
    }
    return updateDoc(ref(COLLECTIONS.seasons, seasonId), payload);
  },

  /** Admin approves season -> moves to approved state (host can setup quizzes) */
  approveSeason: async (seasonId, adminId) => {
    return updateDoc(ref(COLLECTIONS.seasons, seasonId), {
      status: 'approved',
      approvedAt: serverTimestamp(),
      approvedBy: adminId || 'admin',
      rejectionReason: null,
      rejectedAt: null,
    });
  },

  /** Admin rejects season -> moves to rejected state */
  rejectSeason: async (seasonId, { reason, adminId }) => {
    return updateDoc(ref(COLLECTIONS.seasons, seasonId), {
      status: 'rejected',
      rejectionReason: reason || 'Does not meet contest quality guidelines.',
      rejectedAt: serverTimestamp(),
      rejectedBy: adminId || 'admin',
    });
  },

  /** Host or Admin publishes season -> moves to active (live on mobile app) */
  activateSeason: async (seasonId, activatedBy) => {
    return updateDoc(ref(COLLECTIONS.seasons, seasonId), {
      status: 'active',
      activatedAt: serverTimestamp(),
      activatedBy: activatedBy || 'host',
      suspensionReason: null,
      suspendedAt: null,
    });
  },

  /** Host or Admin completes season */
  completeSeason: async (seasonId) => {
    return updateDoc(ref(COLLECTIONS.seasons, seasonId), {
      status: 'completed',
      completedAt: serverTimestamp(),
    });
  },

  /** Admin suspends season for policy violation */
  suspendSeason: async (seasonId, { reason, adminId }) => {
    return updateDoc(ref(COLLECTIONS.seasons, seasonId), {
      status: 'suspended',
      suspensionReason: reason || 'Violation of Bible contest guidelines and principles.',
      suspendedAt: serverTimestamp(),
      suspendedBy: adminId || 'admin',
    });
  },

  /** Delete (deactivate) a season document */
  delete: (seasonId) => updateDoc(ref(COLLECTIONS.seasons, seasonId), {
    status: 'deactivated',
    deactivatedAt: serverTimestamp(),
  }),
};

// ─── Quizzes ──────────────────────────────────────────────────────────────────
const quizPath = (seasonId) => `${COLLECTIONS.seasons}/${seasonId}/quizzes`;

export const quizzesService = {
  getAll: async (seasonId) => {
    const q = query(col(quizPath(seasonId)), orderBy('start', 'desc'), limit(10));
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  },

  getOne: async (seasonId, quizId) => {
    const snap = await getDoc(ref(quizPath(seasonId), quizId));
    return snap.exists() ? snap.data() : null;
  },

  create: async (seasonId, data) => {
    if (seasonId) {
      try {
        const seasonSnap = await getDoc(ref(COLLECTIONS.seasons, seasonId));
        if (seasonSnap.exists() && seasonSnap.data()?.type === 'Single Contest') {
          const sData = seasonSnap.data();
          if ((sData.quizzes_count || 0) >= 1) {
            throw new Error('Single Contest seasons are limited to 1 quiz.');
          }
          const qSnap = await getDocs(query(col(quizPath(seasonId)), limit(1)));
          if (!qSnap.empty) {
            throw new Error('Single Contest seasons are limited to 1 quiz.');
          }
        }
      } catch (err) {
        if (err.message === 'Single Contest seasons are limited to 1 quiz.') {
          throw err;
        }
      }
    }
    const createdQuiz = await addWithId(col(quizPath(seasonId)), data, 'quiz_id');

    // Update quizzes_count on season document
    if (seasonId) {
      try {
        const sRef = ref(COLLECTIONS.seasons, seasonId);
        const sSnap = await getDoc(sRef);
        if (sSnap.exists()) {
          const currentCount = sSnap.data().quizzes_count;
          if (typeof currentCount === 'number') {
            await updateDoc(sRef, {
              quizzes_count: increment(1),
            });
          } else {
            const totalSnap = await getDocs(col(quizPath(seasonId)));
            await updateDoc(sRef, {
              quizzes_count: totalSnap.size,
            });
          }
        }
      } catch (err) {
        console.warn('Failed to increment quizzes_count on season:', err);
      }
    }

    return createdQuiz;
  },

  update: async (seasonId, quizId, data) => {
    if (data?.status === 'Ongoing') {
      const qSnap = await getDocs(query(col(quizPath(seasonId), quizId, 'questions'), limit(1)));
      if (qSnap.empty) {
        throw new Error('Cannot move quiz to Ongoing because it has 0 questions. Please add questions first.');
      }
    }
    return updateDoc(ref(quizPath(seasonId), quizId), data);
  },

  /** Get participant (results) count for a quiz */
  getParticipantCount: async (seasonId, quizId) => {
    if (!seasonId || !quizId) return 0;
    try {
      const collRef = collection(db, `${quizPath(seasonId)}/${quizId}/results`);
      const snap = await getCountFromServer(collRef);
      return snap.data().count;
    } catch {
      try {
        const snap = await getDocs(collection(db, `${quizPath(seasonId)}/${quizId}/results`));
        return snap.size;
      } catch {
        return 0;
      }
    }
  },

  /** Delete quiz + its questions and results subcollections, rolling back player stats */
  delete: async (seasonId, quizId, onProgress = null) => {
    const basePath = `${quizPath(seasonId)}/${quizId}`;

    // 1. Upfront Check: Check and revert quiz winner if computed
    try {
      const quizDocRef = ref(quizPath(seasonId), quizId);
      const quizSnap = await getDoc(quizDocRef);
      if (quizSnap.exists()) {
        const qData = quizSnap.data();
        if (qData.winner_id && (qData.results_computed === true || qData.results_computed === 'true')) {
          const userRef = ref('users', qData.winner_id);
          const uSnap = await getDoc(userRef);
          if (uSnap.exists()) {
            const currentWins = Number(uSnap.data().first_place_count) || 0;
            await setDoc(userRef, { first_place_count: Math.max(0, currentWins - 1) }, { merge: true });
          }
        }
      }
    } catch (wErr) {
      console.warn('Failed to revert winner on quiz delete:', wErr);
    }

    // 2. Revert and delete all results in results subcollection in controlled sequential chunks
    let totalResults = 0;
    try {
      const resultsSnap = await getDocs(collection(db, `${basePath}/results`));
      totalResults = resultsSnap.size;
      if (!resultsSnap.empty) {
        const resultDocs = resultsSnap.docs;
        const chunkSize = 10;
        if (onProgress) {
          onProgress(0, totalResults);
        }
        for (let i = 0; i < resultDocs.length; i += chunkSize) {
          const chunk = resultDocs.slice(i, i + chunkSize);
          await Promise.allSettled(
            chunk.map((d) => resultsService.resetAttempt(seasonId, quizId, d.id))
          );
          if (onProgress) {
            onProgress(Math.min(i + chunkSize, totalResults), totalResults);
          }
        }
      }
    } catch (err) {
      console.warn('Could not revert results during quiz delete, falling back to batch delete:', err);
      await _deleteSubcollection(`${basePath}/results`);
    }

    // 3. Delete questions subcollection
    await _deleteSubcollection(`${basePath}/questions`);

    // 4. Delete the quiz document itself
    await deleteDoc(ref(quizPath(seasonId), quizId));

    // 5. Decrement quizzes_count on season document
    if (seasonId) {
      try {
        const sRef = ref(COLLECTIONS.seasons, seasonId);
        const sSnap = await getDoc(sRef);
        if (sSnap.exists()) {
          const currentCount = sSnap.data().quizzes_count;
          if (typeof currentCount === 'number') {
            await updateDoc(sRef, {
              quizzes_count: Math.max(0, currentCount - 1),
            });
          } else {
            const remainingSnap = await getDocs(col(quizPath(seasonId)));
            await updateDoc(sRef, {
              quizzes_count: remainingSnap.size,
            });
          }
        }
      } catch (err) {
        console.warn('Failed to decrement quizzes_count on season:', err);
      }
    }

    return { success: true, revertedCount: totalResults };
  },
};

// ─── Questions ────────────────────────────────────────────────────────────────
const questionPath = (seasonId, quizId) =>
  `${quizPath(seasonId)}/${quizId}/questions`;

export const QUESTIONS_PAGE_SIZE = 10;

export const questionsService = {
  /** Fetch one page of questions, ordered by document ID. Pass the previous
   *  page's `cursor` to fetch the next page. */
  getPage: async (seasonId, quizId, cursor = null) => {
    const constraints = [orderBy('__name__'), limit(QUESTIONS_PAGE_SIZE)];
    if (cursor) constraints.splice(1, 0, startAfter(cursor));
    const snap = await getDocs(query(col(questionPath(seasonId, quizId)), ...constraints));
    return {
      questions: snap.docs.map((d) => d.data()),
      cursor: snap.docs[snap.docs.length - 1] || null,
      hasMore: snap.docs.length === QUESTIONS_PAGE_SIZE,
    };
  },

  getOne: async (seasonId, quizId, questionId) => {
    const snap = await getDoc(ref(questionPath(seasonId, quizId), questionId));
    return snap.exists() ? snap.data() : null;
  },

  create: async (seasonId, quizId, data) => {
    const res = await addWithId(col(questionPath(seasonId, quizId)), data, 'question_id');
    await updateDoc(ref(quizPath(seasonId), quizId), { number_of_questions: increment(1) });
    return res;
  },

  update: (seasonId, quizId, questionId, data) =>
    updateDoc(ref(questionPath(seasonId, quizId), questionId), data),

  delete: async (seasonId, quizId, questionId) => {
    await deleteDoc(ref(questionPath(seasonId, quizId), questionId));
    await updateDoc(ref(quizPath(seasonId), quizId), { number_of_questions: increment(-1) });
  },
};

// ─── Quiz Results ─────────────────────────────────────────────────────────────
const resultsPath = (seasonId, quizId) =>
  `${quizPath(seasonId)}/${quizId}/results`;

export const resultsService = {
  getAll: async (seasonId, quizId, limitCount = 100) => {
    const q = query(
      collection(db, resultsPath(seasonId, quizId)),
      limit(limitCount)
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  /**
   * Reset player quiz attempt and revert user profile, season profile,
   * and leaderboard standings increments with zero-clamping safeguards.
   */
  resetAttempt: async (seasonId, quizId, resultId) => {
    if (!seasonId || !quizId || !resultId) return;

    const resultDocRef = ref(resultsPath(seasonId, quizId), resultId);
    const resultSnap = await getDoc(resultDocRef);

    if (!resultSnap.exists()) {
      return;
    }

    const resultData = resultSnap.data();
    const userId = resultData.userId || resultId;
    const scoreNum = Number(resultData.score) || 0;
    const timeNum = Number(resultData.timeTaken) || 0;

    // Determine calendar week & month keys from submission date (matches submitQuizResult)
    let submissionDate = new Date();
    if (resultData.submittedAt) {
      const parsed = new Date(resultData.submittedAt);
      if (!isNaN(parsed.getTime())) submissionDate = parsed;
    } else if (resultData.timestamp?.toDate) {
      submissionDate = resultData.timestamp.toDate();
    }

    const getWeekOfYear = (d) => {
      const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
      const dayNum = date.getUTCDay() || 7;
      date.setUTCDate(date.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
      return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
    };
    const weekId = `week_${submissionDate.getFullYear()}_${getWeekOfYear(submissionDate)}`;
    const monthId = `month_${submissionDate.getFullYear()}_${submissionDate.getMonth() + 1}`;

    const rollbackTasks = [];

    // 1. Revert user profile (users/{userId}) with zero-clamping
    rollbackTasks.push((async () => {
      try {
        const userRef = ref('users', userId);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const u = userSnap.data();
          const currentTotalScore = Number(u.totalScore ?? u.total_points_scored ?? u.points ?? 0);
          const currentPoints = Number(u.points ?? u.total_points_scored ?? u.totalScore ?? 0);
          const currentQuizzes = Number(u.quizzesPlayed ?? u.total_quizzes_played ?? 0);
          const currentTime = Number(u.total_time_spent ?? 0);

          const newTotalScore = Math.max(0, currentTotalScore - scoreNum);
          const newPoints = Math.max(0, currentPoints - scoreNum);
          const newQuizzes = Math.max(0, currentQuizzes - 1);
          const newTime = Math.max(0, currentTime - timeNum);

          const userUpdatePayload = {
            totalScore: newTotalScore,
            total_points_scored: newTotalScore,
            points: newPoints,
            quizzesPlayed: newQuizzes,
            total_quizzes_played: newQuizzes,
            total_time_spent: newTime,
          };

          // CRITICAL: Protect identity fields! NEVER erase name, email, or photo.
          // If the profile document was missing userName or img, preserve/restore from resultData!
          if (!u.userName && resultData.userName) {
            userUpdatePayload.userName = resultData.userName;
          }
          if (!u.img && !u.photoURL && resultData.img) {
            userUpdatePayload.img = resultData.img;
            userUpdatePayload.photoURL = resultData.img;
          }

          await setDoc(userRef, userUpdatePayload, { merge: true });

          // Invalidate profile cache so fresh data is read immediately
          delete userProfileCache[userId];
          if (u.userId) delete userProfileCache[u.userId];
        }
      } catch (err) {
        console.warn(`Failed to revert user profile for ${userId}:`, err);
      }
    })());

    // 2. Revert season member profile (seasons/{seasonId}/members/{userId}) with zero-clamping
    rollbackTasks.push((async () => {
      try {
        const memberRef = ref(`${COLLECTIONS.seasons}/${seasonId}/members`, userId);
        const memberSnap = await getDoc(memberRef);
        if (memberSnap.exists()) {
          const m = memberSnap.data();
          const currentTotalScore = Number(m.total_score ?? 0);
          const currentGames = Number(m.no_of_games_played ?? 0);
          const currentTime = Number(m.time_spent ?? 0);

          const newTotalScore = Math.max(0, currentTotalScore - scoreNum);
          const newGames = Math.max(0, currentGames - 1);
          const newTime = Math.max(0, currentTime - timeNum);

          const memberUpdatePayload = {
            total_score: newTotalScore,
            no_of_games_played: newGames,
            time_spent: newTime,
          };

          // Protect identity in member profile
          if (!m.userName && resultData.userName) {
            memberUpdatePayload.userName = resultData.userName;
          }
          if (!m.img && resultData.img) {
            memberUpdatePayload.img = resultData.img;
          }

          await setDoc(memberRef, memberUpdatePayload, { merge: true });
        }
      } catch (err) {
        console.warn(`Failed to revert season member for ${userId}:`, err);
      }
    })());

    // 3. Revert standings documents across weekly, monthly, and season periods
    const standingPaths = [
      `standings_weekly/${weekId}/users`,
      `standings_monthly/${monthId}/users`,
      `standings_season/${seasonId}/users`,
      `standings_season_weekly/${seasonId}_${weekId}/users`,
      `standings_season_monthly/${seasonId}_${monthId}/users`,
    ];

    standingPaths.forEach((basePath) => {
      rollbackTasks.push((async () => {
        try {
          const sRef = ref(basePath, userId);
          const sSnap = await getDoc(sRef);
          if (sSnap.exists()) {
            const sData = sSnap.data();
            const currentGames = Number(sData.gamesCount) || 1;
            const currentScore = Number(sData.score) || 0;
            const currentTime = Number(sData.timeTaken) || 0;

            // Preserve standings document and user presence, zero-clamping the game score/stats
            await setDoc(sRef, {
              score: Math.max(0, currentScore - scoreNum),
              timeTaken: Math.max(0, currentTime - timeNum),
              gamesCount: Math.max(0, currentGames - 1),
              lastUpdated: serverTimestamp(),
            }, { merge: true });
          }
        } catch (err) {
          console.warn(`Failed to revert standing at ${basePath}/${userId}:`, err);
        }
      })());
    });

    // 4. Check if quiz had computed winner pointing to this user
    rollbackTasks.push((async () => {
      try {
        const quizDocRef = ref(quizPath(seasonId), quizId);
        const quizSnap = await getDoc(quizDocRef);
        if (quizSnap.exists()) {
          const qData = quizSnap.data();
          if (qData.winner_id === userId) {
            // Revert winner count on user
            const userRef = ref('users', userId);
            const uSnap = await getDoc(userRef);
            if (uSnap.exists()) {
              const currentWins = Number(uSnap.data().first_place_count) || 0;
              await setDoc(userRef, { first_place_count: Math.max(0, currentWins - 1) }, { merge: true });
            }
            // Unmark winner on quiz
            await updateDoc(quizDocRef, {
              results_computed: false,
              winner_id: null,
              winner_name: null,
            });
          }
        }
      } catch (err) {
        console.warn(`Failed to check/revert quiz winner for quiz ${quizId}:`, err);
      }
    })());

    // Execute all rollback tasks in parallel
    await Promise.allSettled(rollbackTasks);

    // 5. Finally, permanently delete the result document
    await deleteDoc(resultDocRef);
  },

  delete: async (seasonId, quizId, resultId) => {
    return resultsService.resetAttempt(seasonId, quizId, resultId);
  }
};

// ─── Settings ─────────────────────────────────────────────────────────────────
const upsert = (collectionName, id, data) =>
  setDoc(doc(collection(db, collectionName), id), data, { merge: true });

const getSettings = async (collectionName, seasonId) => {
  const snap = await getDoc(doc(db, collectionName, seasonId));
  return snap.exists() ? snap.data() : null;
};

export const settingsService = {
  getNotification:  (seasonId) => getSettings(COLLECTIONS.topNotification, seasonId),
  setNotification:  (seasonId, data) => upsert(COLLECTIONS.topNotification, seasonId, data),

  getLeaderboard:   (seasonId) => getSettings(COLLECTIONS.leaderboardSetting, seasonId),
  setLeaderboard:   (seasonId, data) => upsert(COLLECTIONS.leaderboardSetting, seasonId, data),
  saveLeaderboard:  (seasonId, data) => upsert(COLLECTIONS.leaderboardSetting, seasonId, data),

  getAds:           (seasonId) => getSettings(COLLECTIONS.adsController, seasonId),
  setAds:           (seasonId, data) => upsert(COLLECTIONS.adsController, seasonId, data),
};

// ─── User Permissions & Team Access Control ──────────────────────────────────
export const userPermissionsService = {
  /**
   * Get user permissions by UID or email.
   * Cached and fetched once on auth bootstrap.
   */
  getPermissions: async (uid, email) => {
    if (!uid && !email) {
      return { role: ROLES.user, isSuperAdmin: false, modulePermissions: DEFAULT_ROLE_PERMISSIONS[ROLES.user] };
    }

    const normalizedEmail = email ? email.trim().toLowerCase() : '';
    const isDesignatedSuperAdmin = Boolean(
      normalizedEmail &&
      OFFICIAL_EMAILS.superAdmins.some((e) => e.toLowerCase() === normalizedEmail)
    );

    try {
      let snapData = null;
      let targetDocId = null;

      // 1. Direct doc lookup by UID
      if (uid) {
        const snap = await getDoc(doc(db, COLLECTIONS.userPermissions, uid));
        if (snap.exists()) {
          snapData = snap.data();
          targetDocId = snap.id;
        }
      }

      // 2. Lookup by email if UID doc didn't exist
      if (!snapData && normalizedEmail) {
        const emailDocSnap = await getDoc(doc(db, COLLECTIONS.userPermissions, normalizedEmail));
        if (emailDocSnap.exists()) {
          snapData = emailDocSnap.data();
          targetDocId = emailDocSnap.id;
        } else {
          const q = query(col(COLLECTIONS.userPermissions), where('email', '==', normalizedEmail), limit(1));
          const snapEmail = await getDocs(q);
          if (!snapEmail.empty) {
            snapData = snapEmail.docs[0].data();
            targetDocId = snapEmail.docs[0].id;
          }
        }
      }

      if (snapData || isDesignatedSuperAdmin) {
        const rawRole = snapData?.role;
        let role = normalizeRole(rawRole);
        if (isDesignatedSuperAdmin || role === ROLES.superAdmin || snapData?.isSuperAdmin) {
          role = ROLES.superAdmin;
        }

        const isSuperAdmin = Boolean(isDesignatedSuperAdmin || snapData?.isSuperAdmin || role === ROLES.superAdmin);
        const fullModulePermissions = {
          ...(DEFAULT_ROLE_PERMISSIONS[role] || DEFAULT_ROLE_PERMISSIONS[ROLES.user]),
          ...(snapData?.modulePermissions || {}),
        };

        // Self-healing: Ensure all missing fields are completed and stored in Firestore
        const completedDoc = {
          uid: uid || snapData?.uid || '',
          email: normalizedEmail || snapData?.email || '',
          displayName: snapData?.displayName || (normalizedEmail ? normalizedEmail.split('@')[0] : 'Admin'),
          role,
          isSuperAdmin,
          modulePermissions: fullModulePermissions,
          status: snapData?.status || 'active',
          assignedBy: snapData?.assignedBy || (isSuperAdmin ? 'system_init' : 'Admin'),
          createdAt: snapData?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        // Auto-write back if any vital field was missing or if designated super admin
        const docWriteId = targetDocId || uid || normalizedEmail;
        const needsSelfHeal = (!snapData?.isSuperAdmin && isSuperAdmin) || !snapData?.modulePermissions || !snapData?.displayName;
        if (docWriteId && needsSelfHeal) {
          setDoc(doc(db, COLLECTIONS.userPermissions, docWriteId), completedDoc, { merge: true }).catch(() => {});
        }

        return {
          id: docWriteId,
          ...completedDoc,
        };
      }
    } catch (err) {
      console.error('Error fetching user permissions:', err);
    }

    // Default unprivileged role (unless designated super admin)
    return {
      role: isDesignatedSuperAdmin ? ROLES.superAdmin : ROLES.user,
      isSuperAdmin: isDesignatedSuperAdmin,
      modulePermissions: DEFAULT_ROLE_PERMISSIONS[isDesignatedSuperAdmin ? ROLES.superAdmin : ROLES.user],
    };
  },

  /** Get all team members in user_permissions collection */
  getAllTeamMembers: async () => {
    try {
      const snap = await getDocs(col(COLLECTIONS.userPermissions));
      return snap.docs.map((d) => {
        const data = d.data();
        const email = data.email ? data.email.trim().toLowerCase() : '';
        const isSuperAdmin = Boolean(
          (email && OFFICIAL_EMAILS.superAdmins.some((e) => e.toLowerCase() === email)) ||
          data.isSuperAdmin ||
          normalizeRole(data.role) === ROLES.superAdmin
        );
        const role = isSuperAdmin ? ROLES.superAdmin : normalizeRole(data.role);

        return {
          id: d.id,
          ...data,
          email,
          role,
          isSuperAdmin,
          displayName: data.displayName || (email ? email.split('@')[0] : 'Member'),
          status: data.status || 'active',
          modulePermissions: {
            ...(DEFAULT_ROLE_PERMISSIONS[role] || DEFAULT_ROLE_PERMISSIONS[ROLES.user]),
            ...(data.modulePermissions || {}),
          },
        };
      });
    } catch (err) {
      console.error('Failed to get team members:', err);
      return [];
    }
  },

  /** Save or update a team member's role and permissions */
  saveTeamMember: async (docId, memberData, requester) => {
    const id = docId || (memberData.uid || memberData.email?.toLowerCase().trim());
    if (!id) throw new Error('Cannot save team member without valid ID, UID or email');
    
    const role = normalizeRole(memberData.role);
    const memberEmail = memberData.email?.toLowerCase().trim();
    const isSuperAdmin = Boolean(
      memberData.isSuperAdmin ||
      role === ROLES.superAdmin ||
      (memberEmail && OFFICIAL_EMAILS.superAdmins.some((e) => e.toLowerCase() === memberEmail))
    );

    // Only Super Admin can promote/modify someone as Super Admin or Admin
    if ((role === ROLES.superAdmin || role === ROLES.admin) && requester && !requester.isSuperAdmin) {
      throw new Error('Only Super Admins have permission to configure or assign Admin roles.');
    }

    const payload = {
      ...memberData,
      email: memberEmail,
      role,
      isSuperAdmin,
      modulePermissions: memberData.modulePermissions || DEFAULT_ROLE_PERMISSIONS[role] || DEFAULT_ROLE_PERMISSIONS[ROLES.user],
      status: memberData.status || 'active',
      updatedAt: new Date().toISOString(),
    };
    if (!payload.createdAt) {
      payload.createdAt = new Date().toISOString();
    }
    
    await setDoc(doc(db, COLLECTIONS.userPermissions, id), payload, { merge: true });
    return { id, ...payload };
  },

  /** Remove a team member (Super Admin protection) */
  removeTeamMember: async (docId, targetMember, requester) => {
    const targetEmail = targetMember?.email?.toLowerCase()?.trim();
    const isTargetAdmin =
      targetMember?.isSuperAdmin ||
      normalizeRole(targetMember?.role) === ROLES.superAdmin ||
      normalizeRole(targetMember?.role) === ROLES.admin ||
      (targetEmail && OFFICIAL_EMAILS.superAdmins.some((e) => e.toLowerCase() === targetEmail));
    if (isTargetAdmin && requester && !requester.isSuperAdmin) {
      throw new Error('Permission denied: Only Super Admins can remove Admin or Super Admin accounts.');
    }
    await deleteDoc(doc(db, COLLECTIONS.userPermissions, docId));
  },

  /** Seed initial Admin if collection is empty */
  seedFirstAdmin: async (user) => {
    if (!user || !user.email) return;
    const docId = user.uid || user.email.toLowerCase().trim();
    const adminData = {
      uid: user.uid,
      email: user.email.toLowerCase().trim(),
      displayName: user.displayName || 'Super Admin',
      role: ROLES.superAdmin,
      isSuperAdmin: true,
      modulePermissions: DEFAULT_ROLE_PERMISSIONS[ROLES.superAdmin],
      status: 'active',
      assignedBy: 'system_init',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    await setDoc(doc(db, COLLECTIONS.userPermissions, docId), adminData, { merge: true });
    return adminData;
  },
};

/** Backward compatibility helper for legacy callers */
export const getUserRole = async (uid) => {
  const perm = await userPermissionsService.getPermissions(uid);
  return perm?.role || null;
};

// ─── User Profile & Player Statistics (Synced with Mobile App) ─────────────────
export const usersService = {
  /** Generate clean 6-character referral code (matches mobile ReferralService) */
  generateReferralCode: (userName, userId) => {
    let prefix = '';
    if (userName && userName.trim().length > 0) {
      const parts = userName.trim().split(/\s+/);
      if (parts.length >= 2) {
        prefix = (parts[0][0] + parts[1][0]).toUpperCase();
      } else {
        prefix = userName.trim().substring(0, Math.min(3, userName.trim().length)).toUpperCase();
      }
    }
    prefix = prefix.replace(/[^A-Z0-9]/g, '');
    if (prefix.length < 2) prefix = 'BC';

    const chars = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
    let randomPart = '';
    for (let i = 0; i < (6 - prefix.length); i++) {
      randomPart += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return `${prefix}${randomPart}`;
  },

  /**
   * Fetch complete user profile document, initializing if new
   */
  getUserProfile: async (userId, fallbackAuthUser = null) => {
    if (!userId) return null;

    try {
      const userRef = doc(db, COLLECTIONS.users, userId);
      const snap = await getDoc(userRef);

      if (snap.exists()) {
        const d = snap.data();
        let referralCode = d.referralCode || '';

        const fallbackName = fallbackAuthUser?.displayName || (fallbackAuthUser?.email ? fallbackAuthUser.email.split('@')[0] : '');
        const fallbackEmail = fallbackAuthUser?.email || '';
        const fallbackImg = fallbackAuthUser?.photoURL || '';

        const updatesToBackfill = {};
        if (!d.userName && fallbackName) updatesToBackfill.userName = fallbackName;
        if (!d.emailId && !d.email && fallbackEmail) {
          updatesToBackfill.emailId = fallbackEmail;
          updatesToBackfill.email = fallbackEmail;
        }
        if (!d.img && !d.photoURL && fallbackImg) {
          updatesToBackfill.img = fallbackImg;
          updatesToBackfill.photoURL = fallbackImg;
        }

        // If referral code missing on existing user, backfill it
        if (!referralCode) {
          referralCode = usersService.generateReferralCode(d.userName || fallbackName, userId);
          updatesToBackfill.referralCode = referralCode;
          updatesToBackfill.referralStats = d.referralStats || {
            invitesCount: 0,
            signupsCount: 0,
            activePlayersCount: 0,
            claimedMilestones: [],
          };
        }

        if (Object.keys(updatesToBackfill).length > 0) {
          setDoc(userRef, updatesToBackfill, { merge: true }).catch((err) => console.warn('Could not backfill user profile fields:', err));
        }

        const resolvedUserName = d.userName || fallbackName || (d.emailId ? d.emailId.split('@')[0] : (d.email ? d.email.split('@')[0] : 'Player'));
        const resolvedEmail = d.emailId || d.email || fallbackEmail;
        const resolvedImg = d.img || d.photoURL || fallbackImg;

        return {
          id: snap.id,
          userId: d.userId || snap.id,
          userName: resolvedUserName,
          emailId: resolvedEmail,
          img: resolvedImg,
          points: d.points || 0,
          withdrew: d.withdrew || 0,
          active: d.active !== undefined ? d.active : 1,

          // Demographics
          country: d.country || '',
          countryCode: d.countryCode || '',
          phone: d.phone || '',
          timezone: d.timezone || '',

          // Streaks
          currentStreak: d.current_streak ?? d.currentStreak ?? 0,
          highestStreak: d.highest_streak ?? d.highestStreak ?? 0,
          lastStreakUpdate: d.last_streak_update || null,
          lastLogin: d.lastLogin || null,

          // Stats
          totalQuizzesPlayed: d.total_quizzes_played ?? d.totalQuizzesPlayed ?? 0,
          totalQuestionsAttempted: d.total_questions_attempted ?? d.totalQuestionsAttempted ?? 0,
          averageAccuracy: Number(((d.average_accuracy ?? d.averageAccuracy ?? 0)).toFixed(1)),
          firstPlaceCount: d.first_place_count ?? d.first_places_count ?? d.firstPlaceCount ?? 0,
          totalPointsScored: d.total_points_scored !== undefined ? d.total_points_scored : (d.totalPointsScored ?? (d.points || 0)),
          totalTimeSpent: d.total_time_spent ?? d.totalTimeSpent ?? 0,

          // Referrals
          referralCode,
          referredBy: d.referredBy || null,
          referralStats: {
            invitesCount: d.referralStats?.invitesCount || 0,
            signupsCount: d.referralStats?.signupsCount || 0,
            activePlayersCount: d.referralStats?.activePlayersCount || 0,
            claimedMilestones: d.referralStats?.claimedMilestones || [],
          },

          // Moderation
          isSuspended: d.isSuspended === true || d.status === 'suspended',
          status: d.status || (d.isSuspended ? 'suspended' : 'active'),
          suspensionReason: d.suspensionReason || null,
          createdAt: d.createdAt || null,
        };
      }

      // If document doesn't exist yet, auto-provision for new player
      if (fallbackAuthUser) {
        const generatedCode = usersService.generateReferralCode(fallbackAuthUser.displayName, userId);
        const newDoc = {
          userId,
          userName: fallbackAuthUser.displayName || (fallbackAuthUser.email ? fallbackAuthUser.email.split('@')[0] : 'Player'),
          emailId: fallbackAuthUser.email || '',
          img: fallbackAuthUser.photoURL || '',
          points: 0,
          withdrew: 0,
          active: 1,
          country: '',
          countryCode: '',
          phone: '',
          timezone: '',
          current_streak: 0,
          highest_streak: 0,
          total_quizzes_played: 0,
          total_questions_attempted: 0,
          average_accuracy: 0.0,
          first_place_count: 0,
          total_points_scored: 0,
          total_time_spent: 0,
          referralCode: generatedCode,
          referralStats: {
            invitesCount: 0,
            signupsCount: 0,
            activePlayersCount: 0,
            claimedMilestones: [],
          },
          status: 'active',
          isSuspended: false,
          createdAt: serverTimestamp(),
          lastLogin: serverTimestamp(),
        };

        await setDoc(userRef, newDoc, { merge: true });

        return {
          id: userId,
          ...newDoc,
          currentStreak: 0,
          highestStreak: 0,
          totalQuizzesPlayed: 0,
          totalQuestionsAttempted: 0,
          averageAccuracy: 0.0,
          firstPlaceCount: 0,
          totalPointsScored: 0,
          totalTimeSpent: 0,
        };
      }

      return null;
    } catch (err) {
      console.error('Failed to get user profile:', err);
      return null;
    }
  },

  /**
   * Update profile fields (Display Name, Demographics, Avatar)
   */
  updateUserProfile: async (userId, data) => {
    if (!userId) throw new Error('userId is required');
    const userRef = doc(db, COLLECTIONS.users, userId);

    const payload = {
      ...data,
      updatedAt: serverTimestamp(),
    };

    await setDoc(userRef, payload, { merge: true });
    return payload;
  },

  /**
   * Upload profile avatar image to Firebase Storage
   */
  uploadProfileImage: async (userId, file) => {
    if (!userId || !file) throw new Error('userId and file are required');
    const imageRef = storageRef(storage, `users/${userId}/profile_image_${Date.now()}.jpg`);
    const snapshot = await uploadBytes(imageRef, file, { contentType: file.type || 'image/jpeg' });
    const downloadURL = await getDownloadURL(snapshot.ref);

    // Update Firestore doc
    await setDoc(doc(db, COLLECTIONS.users, userId), {
      img: downloadURL,
      updatedAt: serverTimestamp(),
    }, { merge: true });

    return downloadURL;
  },

  /**
   * Get user's recent contest activity from results collectionGroup
   */
  getRecentGames: async (userId, limitCount = 5) => {
    if (!userId) return [];
    try {
      const q = query(
        collectionGroup(db, 'results'),
        where('userId', '==', userId),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => {
        const data = d.data();
        const quizTitle = data.title || (data.quiz_id ? data.quiz_id.replace(/_/g, ' ').toUpperCase() : 'Bible Quiz');
        return {
          id: d.id,
          title: quizTitle,
          quizId: data.quiz_id || '',
          score: data.score || 0,
          timeTaken: data.timeTaken || 0,
          timestamp: data.timestamp || null,
        };
      });
    } catch (err) {
      console.warn('Could not load recent games via collectionGroup:', err);
      return [];
    }
  },
};

// ─── Account Deletion Requests ────────────────────────────────────────────────
export const accountDeletionsService = {
  /**
   * 1. Check if user already has an active pending request (Duplicate Prevention)
   */
  getActiveRequestForUser: async (userId, userEmail) => {
    if (!userId && !userEmail) return null;

    // A. Check by userId / uid
    if (userId) {
      for (const field of ['userId', 'uid']) {
        try {
          const q = query(
            col(COLLECTIONS.accountDeletions),
            where(field, '==', userId),
            limit(10)
          );
          const snap = await getDocs(q);
          const match = snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .find((d) => d.status === 'pending');
          if (match) return match;
        } catch (e) {
          console.warn(`Query ${field} failed:`, e);
        }
      }
    }

    // B. Check by userEmail / email
    if (userEmail) {
      for (const field of ['userEmail', 'email']) {
        try {
          const q = query(
            col(COLLECTIONS.accountDeletions),
            where(field, '==', userEmail),
            limit(10)
          );
          const snap = await getDocs(q);
          const match = snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .find((d) => d.status === 'pending');
          if (match) return match;
        } catch (e) {
          console.warn(`Query ${field} failed:`, e);
        }
      }
    }

    return null;
  },

  /**
   * 2. Create deletion request (Guarded against duplicates) and dispatch admin alert
   */
  create: async (data) => {
    const now = new Date();
    const effectiveUserId = data.userId || data.uid || '';
    const effectiveEmail = data.userEmail || data.email || '';
    const displayName = data.userName || (effectiveEmail ? effectiveEmail.split('@')[0] : 'User');
    const reasonText = (data.reason || '').trim();

    // Prevent duplicate active requests
    const existing = await accountDeletionsService.getActiveRequestForUser(effectiveUserId, effectiveEmail);
    if (existing) {
      return { id: existing.id, ...existing, isExisting: true };
    }

    const payload = {
      ...data,
      userId: effectiveUserId,
      uid: effectiveUserId,
      userEmail: effectiveEmail,
      email: effectiveEmail,
      userName: displayName,
      userPhoto: data.userPhoto || '',
      reason: reasonText,
      status: data.status || 'pending',
      gracePeriodDays: data.gracePeriodDays !== undefined ? data.gracePeriodDays : 7,
      createdAt: data.createdAt || now,
      requestDate: data.requestDate || now,
    };

    const docRef = await addDoc(col(COLLECTIONS.accountDeletions), payload);
    const createdRequest = { id: docRef.id, ...payload };

    // Dispatch Admin Action Item alert so the separate Admin App receives it
    try {
      await addDoc(col(COLLECTIONS.adminActionItems), {
        title: 'Account Deletion Request',
        message: `User ${effectiveEmail} requested account deletion. Reason: ${reasonText || 'None provided'}`,
        type: 'account_deletion',
        status: 'open',
        senderId: effectiveUserId,
        senderName: displayName,
        readBy: [],
        data: {
          requestId: docRef.id,
          userId: effectiveUserId,
          userEmail: effectiveEmail,
          userName: displayName,
          reason: reasonText,
        },
        createdAt: now,
      });
    } catch (err) {
      console.warn('Could not create admin_action_item:', err);
    }

    return createdRequest;
  },

  /**
   * 3. Self-service cancellation by the user during grace period
   */
  cancelRequest: async (requestId, userId, userEmail) => {
    const now = new Date();

    // A. Update deletion request document
    await updateDoc(ref(COLLECTIONS.accountDeletions, requestId), {
      status: 'cancelled',
      cancelledAt: now,
      cancelledBy: userEmail || userId || 'User',
    });

    // B. Close corresponding open action item in admin_action_items
    try {
      const qAction = query(
        col(COLLECTIONS.adminActionItems),
        where('type', '==', 'account_deletion'),
        where('status', '==', 'open')
      );
      const snap = await getDocs(qAction);
      const matches = snap.docs.filter((d) => {
        const item = d.data();
        return item.data?.requestId === requestId || item.data?.userId === userId || item.senderId === userId;
      });
      await Promise.all(
        matches.map((d) =>
          updateDoc(d.ref, {
            status: 'closed',
            resolvedBy: userEmail || 'User (Cancelled Request)',
            resolvedAt: now,
          })
        )
      );
    } catch (e) {
      console.warn('Could not close action item:', e);
    }
  },

  /**
   * 4. Real-time subscription for deletion requests
   */
  subscribe: (callback, limitCount = 50) => {
    const q = query(
      col(COLLECTIONS.accountDeletions),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    return onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(list);
    });
  },

  getAll: async (limitCount = 50) => {
    const q = query(
      col(COLLECTIONS.accountDeletions),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  getOne: async (requestId) => {
    const snap = await getDoc(ref(COLLECTIONS.accountDeletions, requestId));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  },

  processDeletion: async (requestId, userId, adminEmail, adminNotes = '') => {
    // 1. Update request status to completed
    await updateDoc(ref(COLLECTIONS.accountDeletions, requestId), {
      status: 'completed',
      processedBy: adminEmail || 'Admin',
      processedAt: new Date(),
      adminNotes: adminNotes || ''
    });

    // 2. Delete user document from Firestore (users collection)
    if (userId) {
      try {
        await deleteDoc(ref('users', userId));
      } catch (err) {
        console.warn('Could not delete user doc:', err);
      }
    }

    // 3. Close matching action items in admin_action_items
    try {
      const qAction = query(
        col(COLLECTIONS.adminActionItems),
        where('type', '==', 'account_deletion'),
        where('status', '==', 'open')
      );
      const snap = await getDocs(qAction);
      const matches = snap.docs.filter((d) => {
        const data = d.data();
        return data.data?.requestId === requestId || data.data?.userId === userId || data.senderId === userId;
      });
      await Promise.all(matches.map((d) =>
        updateDoc(d.ref, {
          status: 'closed',
          resolvedBy: adminEmail || 'Admin',
          resolvedAt: new Date()
        })
      ));
    } catch (e) {
      console.warn('Could not close action item:', e);
    }
  },

  rejectRequest: async (requestId, userId, adminEmail, adminNotes = '') => {
    // 1. Update request status to rejected
    await updateDoc(ref(COLLECTIONS.accountDeletions, requestId), {
      status: 'rejected',
      processedBy: adminEmail || 'Admin',
      processedAt: new Date(),
      adminNotes: adminNotes || ''
    });

    // 2. Close matching action items in admin_action_items
    try {
      const qAction = query(
        col(COLLECTIONS.adminActionItems),
        where('type', '==', 'account_deletion'),
        where('status', '==', 'open')
      );
      const snap = await getDocs(qAction);
      const matches = snap.docs.filter((d) => {
        const data = d.data();
        return data.data?.requestId === requestId || data.data?.userId === userId || data.senderId === userId;
      });
      await Promise.all(matches.map((d) =>
        updateDoc(d.ref, {
          status: 'closed',
          resolvedBy: adminEmail || 'Admin',
          resolvedAt: new Date()
        })
      ));
    } catch (e) {
      console.warn('Could not close action item:', e);
    }
  }
};

export const submitDeletionRequest = (data) =>
  accountDeletionsService.create(data);

// ─── Users ────────────────────────────────────────────────────────────────────
const userProfileCache = {}; // module-level cache for profile lookup reads optimization

export const userService = {
  getProfile: async (userId) => {
    if (userProfileCache[userId]) {
      return userProfileCache[userId];
    }
    const snap = await getDoc(doc(db, 'users', userId));
    const data = snap.exists() ? snap.data() : null;
    if (data) {
      userProfileCache[userId] = data;
    }
    return data;
  },

  getProfiles: async (userIds) => {
    const results = {};
    const validIds = (userIds || []).filter((id) => typeof id === 'string' && id.trim().length > 0);
    const missingIds = [];

    // Check cache first
    for (const id of validIds) {
      if (userProfileCache[id]) {
        results[id] = userProfileCache[id];
      } else {
        missingIds.push(id);
      }
    }

    if (missingIds.length === 0) {
      return results;
    }

    // Chunk missingIds into batches of 30
    const CHUNK_SIZE = 30;
    const chunks = [];
    for (let i = 0; i < missingIds.length; i += CHUNK_SIZE) {
      chunks.push(missingIds.slice(i, i + CHUNK_SIZE));
    }

    // Fetch chunks in parallel with individual getDoc fallback
    const fetches = chunks.map(async (chunk) => {
      try {
        const q = query(
          collection(db, 'users'),
          where(documentId(), 'in', chunk)
        );
        const snap = await getDocs(q);
        snap.docs.forEach((docSnap) => {
          const data = docSnap.data();
          userProfileCache[docSnap.id] = data;
          results[docSnap.id] = data;
        });
      } catch (err) {
        console.warn('Batch user profiles fetch failed, trying individual doc reads:', err);
        await Promise.allSettled(
          chunk.map(async (id) => {
            try {
              const docSnap = await getDoc(doc(db, 'users', id));
              if (docSnap.exists()) {
                const data = docSnap.data();
                userProfileCache[id] = data;
                results[id] = data;
              }
            } catch (singleErr) {
              // ignore single read failure
            }
          })
        );
      }
    });

    await Promise.all(fetches);

    // Populate null for any missing IDs
    for (const id of missingIds) {
      if (!results[id]) {
        results[id] = null;
      }
    }

    return results;
  },

  getAll: async () => {
    const snap = await getDocs(collection(db, 'users'));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  searchUsers: async (queryText, limitCount = 20) => {
    if (!queryText || !queryText.trim()) return [];
    const qTrim = queryText.trim();
    const qLower = qTrim.toLowerCase();
    const resultsMap = new Map();

    const addDocResult = (docSnap) => {
      const data = { id: docSnap.id, userId: docSnap.data().userId || docSnap.id, ...docSnap.data() };
      userProfileCache[data.id] = data;
      userProfileCache[data.userId] = data;
      resultsMap.set(data.id, data);
    };

    // 1. Direct Document ID lookup
    try {
      const docSnap = await getDoc(doc(db, 'users', qTrim));
      if (docSnap.exists()) {
        addDocResult(docSnap);
      }
    } catch (e) {
      // ignore
    }

    // 2. Query by emailId prefix
    try {
      const qEmailId = query(
        collection(db, 'users'),
        where('emailId', '>=', qLower),
        where('emailId', '<=', qLower + '\uf8ff'),
        limit(limitCount)
      );
      const emailIdSnap = await getDocs(qEmailId);
      emailIdSnap.docs.forEach(addDocResult);
    } catch (e) {
      // ignore
    }

    // 3. Query by legacy email prefix
    if (resultsMap.size < limitCount) {
      try {
        const qEmail = query(
          collection(db, 'users'),
          where('email', '>=', qLower),
          where('email', '<=', qLower + '\uf8ff'),
          limit(limitCount - resultsMap.size)
        );
        const emailSnap = await getDocs(qEmail);
        emailSnap.docs.forEach(addDocResult);
      } catch (e) {
        // ignore
      }
    }

    // 4. Query by userName
    if (resultsMap.size < limitCount) {
      try {
        const qName = query(
          collection(db, 'users'),
          where('userName', '>=', qTrim),
          where('userName', '<=', qTrim + '\uf8ff'),
          limit(limitCount - resultsMap.size)
        );
        const nameSnap = await getDocs(qName);
        nameSnap.docs.forEach(addDocResult);
      } catch (e) {
        // ignore
      }
    }

    // 5. Query by phone
    if (resultsMap.size < limitCount) {
      try {
        const qPhone = query(
          collection(db, 'users'),
          where('phone', '>=', qTrim),
          where('phone', '<=', qTrim + '\uf8ff'),
          limit(limitCount - resultsMap.size)
        );
        const phoneSnap = await getDocs(qPhone);
        phoneSnap.docs.forEach(addDocResult);
      } catch (e) {
        // ignore
      }
    }

    // 6. Query by displayName
    if (resultsMap.size < limitCount) {
      try {
        const qDisplay = query(
          collection(db, 'users'),
          where('displayName', '>=', qTrim),
          where('displayName', '<=', qTrim + '\uf8ff'),
          limit(limitCount - resultsMap.size)
        );
        const displaySnap = await getDocs(qDisplay);
        displaySnap.docs.forEach(addDocResult);
      } catch (e) {
        // ignore
      }
    }

    return Array.from(resultsMap.values());
  },

  /** Suspend a user from the platform */
  suspendUser: async (userId, { reason, adminId, until = null }) => {
    const userRef = doc(db, 'users', userId);
    const updatePayload = {
      isSuspended: true,
      status: 'suspended',
      suspensionReason: reason || 'Violation of terms and community guidelines.',
      suspendedAt: serverTimestamp(),
      suspendedBy: adminId || 'admin',
      suspensionUntil: until || null
    };
    await updateDoc(userRef, updatePayload);
    // Invalidate local profile cache
    if (userProfileCache[userId]) {
      userProfileCache[userId] = { ...userProfileCache[userId], ...updatePayload };
    }
    return true;
  },

  /** Unsuspend / Reactivate a user */
  unsuspendUser: async (userId, adminId) => {
    const userRef = doc(db, 'users', userId);
    const updatePayload = {
      isSuspended: false,
      status: 'active',
      suspensionReason: null,
      suspendedAt: null,
      suspendedBy: null,
      suspensionUntil: null
    };
    await updateDoc(userRef, updatePayload);
    // Invalidate local profile cache
    if (userProfileCache[userId]) {
      userProfileCache[userId] = { ...userProfileCache[userId], ...updatePayload };
    }
    return true;
  },

  /** Increment or update reports count for a user */
  updateUserReportsCount: async (userId, incrementVal = 1) => {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      reportsCount: increment(incrementVal)
    });
    if (userProfileCache[userId]) {
      userProfileCache[userId].reportsCount = (userProfileCache[userId].reportsCount || 0) + incrementVal;
    }
    return true;
  }
};

// ─── Members ──────────────────────────────────────────────────────────────────
const memberPath = (seasonId) => `${COLLECTIONS.seasons}/${seasonId}/members`;

export const membersService = {
  getAll: async (seasonId) => {
    const snap = await getDocs(collection(db, memberPath(seasonId)));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  getOne: async (seasonId, userId) => {
    const snap = await getDoc(doc(db, memberPath(seasonId), userId));
    return snap.exists() ? snap.data() : null;
  },

  createOrUpdate: async (seasonId, userId, data) => {
    await setDoc(doc(db, memberPath(seasonId), userId), data, { merge: true });
    return { id: userId, ...data };
  },

  update: async (seasonId, userId, data) => {
    await updateDoc(doc(db, memberPath(seasonId), userId), data);
  },

  delete: async (seasonId, userId) => {
    await deleteDoc(doc(db, memberPath(seasonId), userId));
  }
};

// ─── Chats / Messaging ────────────────────────────────────────────────────────
export const chatsService = {
  subscribeMessages: (seasonId, memberId, callback, limitCount = 100) => {
    const q = query(
      collection(db, `${COLLECTIONS.seasons}/${seasonId}/members/${memberId}/messages`),
      orderBy('timestamp', 'asc'),
      limit(limitCount)
    );
    return onSnapshot(q, (snap) => {
      const messages = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(messages);
    });
  },

  sendMessage: async (seasonId, memberId, senderId, senderName, text, imageUrl = '') => {
    await addDoc(
      collection(db, `${COLLECTIONS.seasons}/${seasonId}/members/${memberId}/messages`),
      {
        senderId,
        senderName,
        text: text || '',
        imageUrl: imageUrl || '',
        seen: false,
        timestamp: new Date()
      }
    );
  },

  markAllAsRead: async (seasonId, memberId, readerId) => {
    const q = query(
      collection(db, `${COLLECTIONS.seasons}/${seasonId}/members/${memberId}/messages`),
      where('seen', '==', false)
    );
    const snap = await getDocs(q);
    const batchPromises = [];
    snap.docs.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.senderId !== readerId) {
        batchPromises.push(updateDoc(docSnap.ref, { seen: true }));
      }
    });
    await Promise.all(batchPromises);
  }
};

// ─── Announcements / Broadcasts ────────────────────────────────────────────────
export const announcementsService = {
  subscribe: (seasonId, callback, limitCount = 50) => {
    const q = query(
      collection(db, `${COLLECTIONS.seasons}/${seasonId}/announcements`),
      orderBy('timestamp', 'asc'),
      limit(limitCount)
    );
    return onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(list);
    });
  },

  create: async (seasonId, { title, content, imageUrl, senderName }) => {
    await addDoc(
      collection(db, `${COLLECTIONS.seasons}/${seasonId}/announcements`),
      {
        title,
        content,
        imageUrl: imageUrl || '',
        senderName: senderName || 'Host',
        timestamp: new Date()
      }
    );
  },

  delete: async (seasonId, annId) => {
    await deleteDoc(doc(db, `${COLLECTIONS.seasons}/${seasonId}/announcements`, annId));
  }
};

// ─── Firebase Storage Service ─────────────────────────────────────────────────
export const storageService = {
  upload: async (path, file) => {
    const sRef = storageRef(storage, path);
    await uploadBytes(sRef, file);
    return getDownloadURL(sRef);
  }
};


// ─── User Notifications ────────────────────────────────────────────────────────
export const notificationsService = {
  /** Create a user notification */
  create: async (data) => {
    return addWithId(col(COLLECTIONS.userNotifications), {
      ...data,
      read: false,
      createdAt: new Date()
    }, 'notification_id');
  },

  /** Get all notifications for a user, sorted by createdAt desc */
  getForUser: async (userId) => {
    const q = query(
      col(COLLECTIONS.userNotifications),
      where('to', '==', userId),
      orderBy('createdAt', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  },

  /** Subscribe to real-time notification changes for a user */
  subscribeForUser: (userId, callback) => {
    const q = query(
      col(COLLECTIONS.userNotifications),
      where('to', '==', userId),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => d.data());
      callback(list);
    });
  },

  /** Mark a single notification as read */
  markAsRead: (notificationId) =>
    updateDoc(ref(COLLECTIONS.userNotifications, notificationId), { read: true }),

  /** Mark all unread notifications for a user as read */
  markAllAsRead: async (userId) => {
    const q = query(
      col(COLLECTIONS.userNotifications),
      where('to', '==', userId),
      where('read', '==', false)
    );
    const snap = await getDocs(q);
    const batchPromises = snap.docs.map((docSnap) =>
      updateDoc(docSnap.ref, { read: true })
    );
    await Promise.all(batchPromises);
  },

  /** Delete a notification */
  delete: (notificationId) =>
    deleteDoc(ref(COLLECTIONS.userNotifications, notificationId)),
};


// ─── Global Announcements / Broadcasts ─────────────────────────────────────────
export const globalAnnouncementsService = {
  subscribe: (callback, limitCount = 50) => {
    const q = query(
      col(COLLECTIONS.globalAnnouncements),
      orderBy('timestamp', 'asc'),
      limit(limitCount)
    );
    return onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(list);
    });
  },

  create: async ({ title, content, imageUrl, senderName }) => {
    await addDoc(
      col(COLLECTIONS.globalAnnouncements),
      {
        title,
        content,
        imageUrl: imageUrl || '',
        senderName: senderName || 'Admin',
        timestamp: new Date()
      }
    );
  },

  delete: async (annId) => {
    await deleteDoc(ref(COLLECTIONS.globalAnnouncements, annId));
  }
};

// ─── Global Support Chats ──────────────────────────────────────────────────────
export const globalChatsService = {
  subscribeConversations: (callback, limitCount = 50) => {
    const q = query(
      col(COLLECTIONS.globalChats),
      orderBy('updatedAt', 'desc'),
      limit(limitCount)
    );
    return onSnapshot(q, (snap) => {
      const threads = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(threads);
    });
  },

  subscribeMessages: (userId, callback, limitCount = 100) => {
    const q = query(
      collection(db, `${COLLECTIONS.globalChats}/${userId}/messages`),
      orderBy('timestamp', 'asc'),
      limit(limitCount)
    );
    return onSnapshot(q, (snap) => {
      const messages = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(messages);
    });
  },

  sendMessage: async (userId, senderId, senderName, text, imageUrl = '', userProfile = null) => {
    // 1. Add message document
    await addDoc(
      collection(db, `${COLLECTIONS.globalChats}/${userId}/messages`),
      {
        senderId,
        senderName,
        text: text || '',
        imageUrl: imageUrl || '',
        seen: false,
        timestamp: new Date()
      }
    );

    // 2. Upsert thread summary details in parent document
    const threadData = {
      userId,
      lastMessage: imageUrl ? '[Image]' : (text || ''),
      updatedAt: new Date(),
    };
    
    if (userProfile) {
      const nameVal = userProfile.name || userProfile.userName || userProfile.displayName;
      if (nameVal) {
        threadData.userName = nameVal;
        threadData.name = nameVal;
      }
      if (userProfile.email) threadData.userEmail = userProfile.email;
      if (userProfile.photoURL) threadData.userImg = userProfile.photoURL;
    }

    await setDoc(doc(db, COLLECTIONS.globalChats, userId), threadData, { merge: true });

    // 3. If the user sent the message (not admin replying), create an admin action item alert
    if (senderId === userId) {
      try {
        await adminActionItemsService.create({
          type: ADMIN_ACTION_TYPES.globalChat,
          title: `Support Message from ${senderName || 'User'}`,
          message: text || (imageUrl ? 'Sent an attachment' : 'New support inquiry'),
          senderId: userId,
          senderName: senderName || 'User',
          data: {
            userId,
            userEmail: userProfile?.email || '',
            userImg: userProfile?.photoURL || ''
          }
        });
      } catch (err) {
        console.warn('Failed to log admin action item for chat message:', err);
      }
    }
  },

  markAllAsRead: async (userId, readerId) => {
    const q = query(
      collection(db, `${COLLECTIONS.globalChats}/${userId}/messages`),
      where('seen', '==', false)
    );
    const snap = await getDocs(q);
    const batchPromises = [];
    snap.docs.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.senderId !== readerId) {
        batchPromises.push(updateDoc(docSnap.ref, { seen: true }));
      }
    });
    await Promise.all(batchPromises);
  },

  searchConversations: async (queryText, limitCount = 50) => {
    if (!queryText || !queryText.trim()) return [];
    const qTrim = queryText.trim();
    const qLower = qTrim.toLowerCase();
    const resultsMap = new Map();

    const addDocResult = (docSnap) => {
      if (!docSnap || !docSnap.exists()) return;
      const data = { id: docSnap.id, ...docSnap.data() };
      resultsMap.set(data.id, data);
    };

    // 1. Direct Document ID lookup (e.g. userId / chat thread ID)
    try {
      const directDoc = await getDoc(doc(db, COLLECTIONS.globalChats, qTrim));
      if (directDoc.exists()) {
        addDocResult(directDoc);
      }
    } catch (e) {
      // ignore
    }

    // 2. Query conversations from Firestore DB (ordered by recent updatedAt)
    try {
      const q = query(
        col(COLLECTIONS.globalChats),
        orderBy('updatedAt', 'desc'),
        limit(limitCount)
      );
      const snap = await getDocs(q);
      snap.docs.forEach((d) => {
        const data = { id: d.id, ...d.data() };
        const name = (data.name || '').toLowerCase();
        const userName = (data.userName || '').toLowerCase();
        const displayName = (data.displayName || '').toLowerCase();
        const email = (data.userEmail || '').toLowerCase();
        const lastMsg = (data.lastMessage || '').toLowerCase();
        const id = (data.id || '').toLowerCase();
        const userId = (data.userId || '').toLowerCase();

        if (
          name.includes(qLower) ||
          userName.includes(qLower) ||
          displayName.includes(qLower) ||
          email.includes(qLower) ||
          lastMsg.includes(qLower) ||
          id.includes(qLower) ||
          userId.includes(qLower)
        ) {
          resultsMap.set(data.id, data);
        }
      });
    } catch (e) {
      console.error('Error querying global chats:', e);
    }

    // 3. Search platform users (name, email, phone, UID) and merge
    try {
      const foundUsers = await userService.searchUsers(qTrim, 25);
      for (const u of foundUsers) {
        const uid = u.userId || u.id;
        if (!uid) continue;

        if (!resultsMap.has(uid)) {
          try {
            const chatSnap = await getDoc(doc(db, COLLECTIONS.globalChats, uid));
            if (chatSnap.exists()) {
              addDocResult(chatSnap);
            } else {
              resultsMap.set(uid, {
                id: uid,
                userId: uid,
                name: u.userName || u.name || u.displayName || u.emailId || u.email || 'Contestant',
                userName: u.userName || u.name || u.displayName,
                userEmail: u.emailId || u.email || '',
                userImg: u.img || u.photoURL || u.profileImage || '',
                lastMessage: 'Start a new conversation',
                isNewCandidate: true
              });
            }
          } catch (err) {
            resultsMap.set(uid, {
              id: uid,
              userId: uid,
              name: u.userName || u.name || u.displayName || u.emailId || u.email || 'Contestant',
              userName: u.userName || u.name || u.displayName,
              userEmail: u.emailId || u.email || '',
              userImg: u.img || u.photoURL || u.profileImage || '',
              lastMessage: 'Start a new conversation',
              isNewCandidate: true
            });
          }
        }
      }
    } catch (err) {
      console.warn('Error querying users for chat:', err);
    }

    return Array.from(resultsMap.values());
  }
};


// ─── Admin Action Items ───────────────────────────────────────────────────────
export const adminActionItemsService = {
  /** Create a new action item */
  create: async (data) => {
    return addWithId(col(COLLECTIONS.adminActionItems), {
      ...data,
      status: data.status || 'open',
      readBy: data.readBy || [],
      createdAt: new Date()
    }, 'action_item_id');
  },

  /** Subscribe to recent action items with limit */
  subscribe: (callback, limitCount = 20) => {
    const q = query(
      col(COLLECTIONS.adminActionItems),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    return onSnapshot(q, (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(items);
    });
  },

  /** Subscribe to all action items for workspace alerts manager (limited to 20) */
  subscribeAll: (callback, limitCount = 20) => {
    const q = query(
      col(COLLECTIONS.adminActionItems),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    return onSnapshot(q, (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(items);
    }, (err) => {
      console.warn('adminActionItemsService subscribeAll error:', err);
      // Fallback with limit if index not present
      const fallbackQ = query(col(COLLECTIONS.adminActionItems), limit(limitCount));
      getDocs(fallbackQ).then((snap) => {
        const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        callback(items);
      }).catch(e => console.error('Fallback getDocs error:', e));
    });
  },

  /** Mark an action item as read by a specific admin */
  markAsRead: async (actionItemId, adminId) => {
    if (!adminId || !actionItemId) return;
    return updateDoc(ref(COLLECTIONS.adminActionItems, actionItemId), {
      readBy: arrayUnion(adminId)
    });
  },

  /** Mark an action item as unread by a specific admin */
  markAsUnread: async (actionItemId, adminId) => {
    if (!adminId || !actionItemId) return;
    return updateDoc(ref(COLLECTIONS.adminActionItems, actionItemId), {
      readBy: arrayRemove(adminId)
    });
  },

  /** Update status (open, closed, resolved) */
  updateStatus: async (actionItemId, status, adminId) => {
    if (!actionItemId) return;
    const updates = {
      status,
      updatedAt: new Date(),
    };
    if (status === 'resolved' || status === 'closed') {
      updates.resolvedBy = adminId || 'admin';
      updates.resolvedAt = new Date();
    }
    if (adminId) {
      updates.readBy = arrayUnion(adminId);
    }
    return updateDoc(ref(COLLECTIONS.adminActionItems, actionItemId), updates);
  },

  /** Close an action item (e.g. when opening a chat item) */
  closeItem: async (actionItemId, adminId) => {
    const updates = {
      status: 'closed',
      resolvedBy: adminId || 'admin',
      resolvedAt: new Date()
    };
    if (adminId) {
      updates.readBy = arrayUnion(adminId);
    }
    return updateDoc(ref(COLLECTIONS.adminActionItems, actionItemId), updates);
  },

  /** Resolve an action item (e.g. account deletion) */
  resolveItem: async (actionItemId, adminId) => {
    const updates = {
      status: 'resolved',
      resolvedBy: adminId || 'admin',
      resolvedAt: new Date()
    };
    if (adminId) {
      updates.readBy = arrayUnion(adminId);
    }
    return updateDoc(ref(COLLECTIONS.adminActionItems, actionItemId), updates);
  },

  /** Mark all unread action items as read for this admin */
  markAllAsRead: async (adminId, actionItems = []) => {
    if (!adminId) return;
    const unread = actionItems.filter((item) => !item.readBy?.includes(adminId));
    const promises = unread.map((item) =>
      updateDoc(ref(COLLECTIONS.adminActionItems, item.id || item.action_item_id), {
        readBy: arrayUnion(adminId)
      })
    );
    await Promise.all(promises);
  },

  /** Delete an action item */
  delete: (actionItemId) =>
    deleteDoc(ref(COLLECTIONS.adminActionItems, actionItemId))
};

// ─── Quiz Winner & Ranking Computation Service ──────────────────────────────
const BASELINE_START_DATE = new Date('2026-08-28T00:00:00+05:30');

export const quizWinnerComputationService = {
  BASELINE_START_DATE,

  /** 
   * Scan and find all eligible finished quizzes needing winner computation (Lightweight, NO result reads):
   * 1. Season excludeFromRanking !== true
   * 2. Quiz status is Finished
   * 3. Quiz start date is after 28 Aug 2026
   * 4. Quiz end date <= last Sunday (if today is Sunday, uses previous week's Sunday)
   * 5. Quiz results_computed !== true
   */
  getEligibleQuizzes: async (customCutoffDate = null) => {
    const eligibleList = [];
    const baselineStartMs = BASELINE_START_DATE.getTime();
    const lastSundayCutoff = customCutoffDate ? new Date(customCutoffDate) : getLastSundayCutoff(new Date());
    const lastSundayCutoffMs = lastSundayCutoff.getTime();

    try {
      const allSeasons = await seasonsService.getAll();
      const validSeasons = allSeasons.filter(
        (s) => s.status !== 'deactivated' && s.excludeFromRanking !== true && s.exclude_from_ranking !== true
      );

      for (const season of validSeasons) {
        const seasonId = season.season_id || season.id;
        if (!seasonId) continue;

        try {
          const qQuizzes = query(col(quizPath(seasonId)));
          const quizSnap = await getDocs(qQuizzes);

          for (const qDoc of quizSnap.docs) {
            const quiz = qDoc.data();
            const quizId = qDoc.id || quiz.quiz_id;

            // 1. Skip if results already computed
            if (quiz.results_computed === true || quiz.results_computed === 'true') {
              continue;
            }

            // 2. Status must be Finished
            const statusLower = (quiz.status || '').toLowerCase().trim();
            if (statusLower !== 'finished') {
              continue;
            }

            // 3. Extract and parse start and end timestamps
            const rawStart = quiz.start || quiz.startDate || quiz.start_date || quiz.startTime;
            let startMs = null;
            if (rawStart) {
              if (typeof rawStart.toDate === 'function') startMs = rawStart.toDate().getTime();
              else if (rawStart.seconds) startMs = rawStart.seconds * 1000;
              else if (rawStart instanceof Date) startMs = rawStart.getTime();
              else {
                const parsed = new Date(rawStart).getTime();
                if (!isNaN(parsed)) startMs = parsed;
              }
            }

            const rawEnd = quiz.end || quiz.endDate || quiz.endTime || quiz.end_date;
            let endMs = null;
            if (rawEnd) {
              if (typeof rawEnd.toDate === 'function') endMs = rawEnd.toDate().getTime();
              else if (rawEnd.seconds) endMs = rawEnd.seconds * 1000;
              else if (rawEnd instanceof Date) endMs = rawEnd.getTime();
              else {
                const parsed = new Date(rawEnd).getTime();
                if (!isNaN(parsed)) endMs = parsed;
              }
            }

            // 4. Start date must be after 28 Aug 2026
            if (!startMs || startMs < baselineStartMs) {
              continue;
            }

            // 5. End date must be on or before last Sunday (23:59:59)
            if (!endMs || endMs > lastSundayCutoffMs) {
              continue;
            }

            // Only add quiz metadata — zero results subcollection reads here!
            eligibleList.push({
              seasonId,
              seasonName: season.season || season.name || 'Unnamed Season',
              quizId,
              quizTitle: quiz.title || 'Untitled Quiz',
              quizStart: rawStart,
              quizEnd: rawEnd,
              startMs,
              endMs,
              daysAgo: Math.floor((Date.now() - endMs) / (1000 * 60 * 60 * 24))
            });
          }
        } catch (err) {
          console.warn(`Could not fetch quizzes for season ${seasonId}:`, err);
        }
      }
    } catch (err) {
      console.error('Failed to get eligible quizzes for winner computation:', err);
    }

    return eligibleList;
  },

  /** Get exact #1 Rank Winner by querying score DESC, timeTaken ASC with limit 1 (Exact 1 document read) */
  getQuizWinner: async (seasonId, quizId) => {
    try {
      const q = query(
        collection(db, `${COLLECTIONS.seasons}/${seasonId}/quizzes/${quizId}/results`),
        orderBy('score', 'desc'),
        orderBy('timeTaken', 'asc'),
        limit(1)
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        const d = snap.docs[0];
        const data = d.data();
        return {
          userId: data.userId || d.id,
          userName: data.userName || data.name || 'Anonymous Player',
          userEmail: data.userEmail || data.email || '',
          score: Number(data.score) || 0,
          timeTaken: Number(data.timeTaken) || 0,
          resultId: d.id
        };
      }
    } catch (err) {
      console.warn(`Composite index query not available for quiz ${quizId}, trying score-only fallback:`, err?.message);
      // Fallback 1: orderBy score desc limit 5
      try {
        const qFallback = query(
          collection(db, `${COLLECTIONS.seasons}/${seasonId}/quizzes/${quizId}/results`),
          orderBy('score', 'desc'),
          limit(5)
        );
        const snap = await getDocs(qFallback);
        if (!snap.empty) {
          const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          list.sort((a, b) => {
            const sDiff = (Number(b.score) || 0) - (Number(a.score) || 0);
            if (sDiff !== 0) return sDiff;
            return (Number(a.timeTaken) || 999999) - (Number(b.timeTaken) || 999999);
          });
          const top = list[0];
          return {
            userId: top.userId || top.id,
            userName: top.userName || top.name || 'Anonymous Player',
            userEmail: top.userEmail || top.email || '',
            score: Number(top.score) || 0,
            timeTaken: Number(top.timeTaken) || 0,
            resultId: top.id
          };
        }
      } catch (fallbackErr) {
        console.warn(`Fallback 1 query failed for quiz ${quizId}, trying plain scan fallback:`, fallbackErr?.message);
        // Fallback 2: plain fetch without order
        try {
          const snap = await getDocs(collection(db, `${COLLECTIONS.seasons}/${seasonId}/quizzes/${quizId}/results`));
          if (!snap.empty) {
            const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            list.sort((a, b) => {
              const sDiff = (Number(b.score) || 0) - (Number(a.score) || 0);
              if (sDiff !== 0) return sDiff;
              return (Number(a.timeTaken) || 999999) - (Number(b.timeTaken) || 999999);
            });
            const top = list[0];
            return {
              userId: top.userId || top.id,
              userName: top.userName || top.name || 'Anonymous Player',
              userEmail: top.userEmail || top.email || '',
              score: Number(top.score) || 0,
              timeTaken: Number(top.timeTaken) || 0,
              resultId: top.id
            };
          }
        } catch (e3) {
          console.error(`All winner fetch attempts failed for quiz ${quizId}:`, e3);
        }
      }
    }
    return null;
  },

  /** Compute winner for a single quiz: Increment profile win count & mark results_computed: true */
  computeWinnerForQuiz: async (quizItem, adminUser) => {
    const seasonId = quizItem.seasonId;
    const quizId = quizItem.quizId;
    if (!seasonId || !quizId) {
      throw new Error(`Missing season ID (${seasonId}) or quiz ID (${quizId})`);
    }

    // 1. Fetch exact #1 winner using targeted query (limit 1)
    const winner = await quizWinnerComputationService.getQuizWinner(seasonId, quizId);

    // 2. Increment first_place_count on winner's user profile if winner exists
    if (winner?.userId) {
      try {
        const userRef = doc(db, 'users', winner.userId);
        await setDoc(userRef, {
          first_place_count: increment(1)
        }, { merge: true });

        // Update local profile cache if present
        if (userProfileCache[winner.userId]) {
          userProfileCache[winner.userId].first_place_count =
            (userProfileCache[winner.userId].first_place_count || 0) + 1;
        }
      } catch (userErr) {
        console.warn(`Could not increment first_place_count for user ${winner.userId}:`, userErr);
      }
    }

    // 3. Mark quiz document with results_computed: true
    const quizDocRef = doc(db, `${COLLECTIONS.seasons}/${seasonId}/quizzes/${quizId}`);
    const updatePayload = {
      results_computed: true,
      results_computed_at: new Date(),
      results_computed_by: adminUser?.email || adminUser?.uid || 'Admin'
    };

    if (winner?.userId) {
      updatePayload.winner_id = winner.userId;
    }
    if (winner?.userName) {
      updatePayload.winner_name = winner.userName;
    }
    if (winner?.score !== undefined) {
      updatePayload.winner_score = winner.score;
    }
    if (winner?.timeTaken !== undefined) {
      updatePayload.winner_time_taken = winner.timeTaken;
    }

    await setDoc(quizDocRef, updatePayload, { merge: true });

    return {
      success: true,
      quizId,
      winner
    };
  },

  /** Compute winners for multiple quizzes in batch and award scores to user profiles */
  computeWinnersBatch: async (quizzesList, adminUser, onProgress) => {
    let processed = 0;
    const total = quizzesList.length;
    const results = [];

    for (let i = 0; i < total; i++) {
      const item = quizzesList[i];
      let res = null;
      try {
        res = await quizWinnerComputationService.computeWinnerForQuiz(item, adminUser);
        results.push(res);
      } catch (err) {
        console.error(`Failed to compute winner for quiz ${item.quizId}:`, err);
      } finally {
        processed++;
        if (onProgress) {
          onProgress({
            processed,
            total,
            remaining: Math.max(0, total - processed),
            percent: Math.round((processed / total) * 100),
            currentQuiz: item,
            winner: res?.winner || null
          });
        }
      }
    }

    return results;
  }
};

// ─── Job & Volunteer Postings Service ─────────────────────────────────────────
export const jobPostingsService = {
  /** Create a new job/volunteer posting */
  create: async (jobData, adminUser = null) => {
    const data = {
      title: jobData.title || '',
      type: jobData.type || 'volunteer', // 'volunteer' or 'full_time'
      category: jobData.category || 'Ministry',
      commitment: jobData.commitment || 'Flexible',
      location: jobData.location || 'Remote',
      icon: jobData.icon || '📖',
      shortDescription: jobData.shortDescription || '',
      responsibilities: Array.isArray(jobData.responsibilities)
        ? jobData.responsibilities
        : (jobData.responsibilities || '').split('\n').map(s => s.trim()).filter(Boolean),
      requirements: Array.isArray(jobData.requirements)
        ? jobData.requirements
        : (jobData.requirements || '').split('\n').map(s => s.trim()).filter(Boolean),
      status: jobData.status || 'active', // 'active' or 'closed'
      createdAt: new Date(),
      updatedAt: new Date(),
      postedBy: adminUser?.email || adminUser?.displayName || 'Admin',
    };
    return addWithId(col(COLLECTIONS.jobPostings), data, 'posting_id');
  },

  /** Update an existing job/volunteer posting */
  update: async (jobId, jobData) => {
    const updates = {
      ...jobData,
      responsibilities: Array.isArray(jobData.responsibilities)
        ? jobData.responsibilities
        : (jobData.responsibilities || '').split('\n').map(s => s.trim()).filter(Boolean),
      requirements: Array.isArray(jobData.requirements)
        ? jobData.requirements
        : (jobData.requirements || '').split('\n').map(s => s.trim()).filter(Boolean),
      updatedAt: new Date(),
    };
    return setDoc(ref(COLLECTIONS.jobPostings, jobId), updates, { merge: true });
  },

  /** Delete a job posting */
  delete: async (jobId) => {
    return deleteDoc(ref(COLLECTIONS.jobPostings, jobId));
  },

  /** Toggle active / closed status */
  toggleStatus: async (jobId, targetStatus) => {
    const status = targetStatus === 'closed' ? 'closed' : 'active';
    return setDoc(ref(COLLECTIONS.jobPostings, jobId), {
      status,
      updatedAt: new Date(),
    }, { merge: true });
  },

  /** Real-time subscription to all job postings (for Admin) */
  subscribe: (callback) => {
    return onSnapshot(
      col(COLLECTIONS.jobPostings),
      (snap) => {
        callback(snap.docs.map((d) => ({ ...d.data(), id: d.id, posting_id: d.id })));
      },
      (err) => {
        console.error('Job postings subscription error:', err);
        callback([]);
      }
    );
  },

  /** Real-time subscription to ONLY ACTIVE job postings for public Volunteer & Careers page */
  subscribeActive: (callback) => {
    const q = query(col(COLLECTIONS.jobPostings), where('status', '==', 'active'));
    return onSnapshot(
      q,
      (snap) => {
        callback(snap.docs.map((d) => ({ ...d.data(), id: d.id, posting_id: d.id })));
      },
      (err) => {
        console.error('Active job postings subscription error:', err);
        callback([]);
      }
    );
  }
};

// ─── Volunteer & Full-Time Applications ───────────────────────────────────────
export const volunteerApplicationsService = {
  submit: async (applicationData) => {
    const data = {
      ...applicationData,
      status: 'pending', // pending, shortlisted, accepted, rejected
      appliedAt: new Date(),
      createdAt: new Date(),
    };
    const result = await addWithId(col(COLLECTIONS.volunteerApplications), data, 'application_id');

    // Notify admins via action items
    try {
      await adminActionItemsService.create({
        type: ADMIN_ACTION_TYPES.volunteerApplication,
        title: `New ${applicationData.roleType === 'full_time' ? 'Full-Time' : 'Volunteer'} Application`,
        message: `${applicationData.fullName} applied for ${applicationData.roleTitle || 'a position'} (${applicationData.email})`,
        senderId: applicationData.userId || applicationData.email,
        senderName: applicationData.fullName,
        data: {
          applicationId: result.id,
          roleId: applicationData.roleId,
          roleTitle: applicationData.roleTitle,
          roleType: applicationData.roleType,
          email: applicationData.email,
          phone: applicationData.phone || '',
        }
      });
    } catch (err) {
      console.warn('Failed to create admin action item for application:', err);
    }

    return result;
  },

  subscribe: (callback, limitCount = 100) => {
    const q = query(
      col(COLLECTIONS.volunteerApplications),
      orderBy('appliedAt', 'desc'),
      limit(limitCount)
    );
    return onSnapshot(q, (snap) => {
      const items = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
      callback(items);
    }, (err) => {
      console.warn('Volunteer applications subscription error:', err);
      getDocs(col(COLLECTIONS.volunteerApplications)).then((snap) => {
        callback(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
      }).catch((e) => console.error('Fallback applications getDocs error:', e));
    });
  },

  updateStatus: async (applicationId, status, notes = '', adminUser = null) => {
    const updates = {
      status, // 'pending', 'shortlisted', 'accepted', 'rejected'
      adminNotes: notes,
      reviewedAt: new Date(),
      updatedAt: new Date(),
    };
    if (adminUser) {
      updates.reviewedBy = adminUser.email || adminUser.displayName || 'Admin';
    }
    return setDoc(ref(COLLECTIONS.volunteerApplications, applicationId), updates, { merge: true });
  },

  delete: async (applicationId) => {
    return deleteDoc(ref(COLLECTIONS.volunteerApplications, applicationId));
  }
};

// ─── Fundraising & Donation Pledges ──────────────────────────────────────────
export const fundraisingService = {
  submitPledge: async (pledgeData) => {
    const data = {
      ...pledgeData,
      status: 'pledged',
      createdAt: new Date(),
    };
    const result = await addWithId(col(COLLECTIONS.fundraisingDonations), data, 'pledge_id');

    // Notify admins
    try {
      await adminActionItemsService.create({
        type: 'donation_pledge',
        title: `Contribution Interest: ${pledgeData.donorName || 'Supporter'}`,
        message: `${pledgeData.donorName || 'Someone'} expressed interest to support (${pledgeData.cause || 'Ministry Support'}). Preferred contact: ${pledgeData.preferredContact || 'Email/Phone'}.`,
        senderId: pledgeData.userId || pledgeData.email || 'guest',
        senderName: pledgeData.donorName || 'Supporter',
        data: {
          pledgeId: result.id,
          cause: pledgeData.cause,
          email: pledgeData.email,
          phone: pledgeData.phone,
          frequency: pledgeData.frequency,
          preferredContact: pledgeData.preferredContact,
          prayerNote: pledgeData.prayerNote,
        }
      });
    } catch (err) {
      console.warn('Failed to notify admin on pledge:', err);
    }

    return result;
  },

  subscribePledges: (callback, limitCount = 50) => {
    const q = query(
      col(COLLECTIONS.fundraisingDonations),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    return onSnapshot(q, (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(items);
    });
  }
};

// ─── Blog & Ministry Articles Service ─────────────────────────────────────────
export const blogService = {
  /**
   * Get all blog posts from Firestore
   */
  getAllPosts: async (includeDrafts = true) => {
    try {
      const snap = await getDocs(col(COLLECTIONS.blogPosts));
      let list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      if (!includeDrafts) {
        list = list.filter((p) => p.status !== 'draft');
      }
      return list.sort((a, b) => (b.publishedAt || '').localeCompare(a.publishedAt || ''));
    } catch (err) {
      console.error('Error fetching blog posts from Firestore:', err);
      return [];
    }
  },

  /**
   * Get a single post by slug or doc ID directly from Firestore
   */
  getPostBySlug: async (slug) => {
    if (!slug) return null;
    try {
      // 1. Try direct doc lookup
      const directDoc = await getDoc(ref(COLLECTIONS.blogPosts, slug));
      if (directDoc.exists()) {
        return { id: directDoc.id, ...directDoc.data() };
      }
      // 2. Try query by slug field
      const q = query(col(COLLECTIONS.blogPosts), where('slug', '==', slug), limit(1));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const d = snap.docs[0];
        return { id: d.id, ...d.data() };
      }
    } catch (err) {
      console.error('Error fetching post by slug from Firestore:', err);
    }
    return null;
  },

  /**
   * Create a new blog post in Firestore
   */
  createPost: async (postData) => {
    const slug = (postData.slug || postData.title || 'post')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

    const newDocRef = doc(col(COLLECTIONS.blogPosts));
    const finalData = {
      ...postData,
      id: newDocRef.id,
      slug: slug || newDocRef.id,
      status: postData.status || 'published',
      publishedAt: postData.publishedAt || new Date().toISOString().split('T')[0],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(newDocRef, finalData);
    return finalData;
  },

  /**
   * Update an existing blog post
   */
  updatePost: async (id, postData) => {
    if (!id) throw new Error('Post ID is required for update');
    const docRef = ref(COLLECTIONS.blogPosts, id);
    const updateData = {
      ...postData,
      updatedAt: serverTimestamp(),
    };
    await updateDoc(docRef, updateData);
    return { id, ...updateData };
  },

  /**
   * Delete a blog post
   */
  deletePost: async (id) => {
    if (!id) throw new Error('Post ID is required for deletion');
    await deleteDoc(ref(COLLECTIONS.blogPosts, id));
    return true;
  },

  /**
   * Toggle published / draft status
   */
  togglePublishStatus: async (id, currentStatus) => {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    await blogService.updatePost(id, { status: newStatus });
    return newStatus;
  }
};

// ─── Analytics & Platform Metrics Service ────────────────────────────────────
export const analyticsService = {
  /**
   * Get server-side document counts for users, collectionGroup quizzes, and seasons
   */
  getOverviewMetrics: async () => {
    let totalUsers = 0;
    let totalQuizzes = 0;
    let totalSeasons = 0;

    // 1. Count registered users from 'users' collection
    try {
      const usersSnap = await getCountFromServer(collection(db, 'users'));
      totalUsers = usersSnap.data().count;
    } catch (e) {
      console.warn('Could not count users from server:', e);
      try {
        const uSnap = await getDocs(collection(db, 'users'));
        totalUsers = uSnap.size;
      } catch (_) {}
    }

    // 2. Count quizzes dispatched across all seasons (collectionGroup 'quizzes')
    try {
      const quizzesSnap = await getCountFromServer(collectionGroup(db, 'quizzes'));
      totalQuizzes = quizzesSnap.data().count;
    } catch (e) {
      console.warn('Could not count collectionGroup quizzes from server:', e);
      try {
        const qSnap = await getDocs(collectionGroup(db, 'quizzes'));
        totalQuizzes = qSnap.size;
      } catch (_) {}
    }

    // 3. Count seasons from 'seasons' collection
    try {
      const seasonsSnap = await getCountFromServer(collection(db, COLLECTIONS.seasons));
      totalSeasons = seasonsSnap.data().count;
    } catch (e) {
      console.warn('Could not count seasons from server:', e);
    }

    return {
      totalUsers,
      totalQuizzes,
      totalSeasons
    };
  }
};

// ─── Referral & Marketing Campaign Service ────────────────────────────────────
export const referralService = {
  /**
   * Create a new marketing referral campaign
   */
  createCampaign: async (campaignData) => {
    const rawCode = (campaignData.code || campaignData.name || 'campaign')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/(^-|-$)/g, '');

    const docRef = doc(col(COLLECTIONS.referralCampaigns), rawCode);
    const existing = await getDoc(docRef);
    if (existing.exists()) {
      throw new Error(`Referral code "${rawCode}" is already in use. Please choose another code.`);
    }

    const finalData = {
      ...campaignData,
      id: rawCode,
      code: rawCode,
      name: campaignData.name || rawCode,
      channel: campaignData.channel || 'other',
      medium: campaignData.medium || 'social',
      targetDestination: campaignData.targetDestination || 'app_store',
      status: campaignData.status || 'active',
      clicksCount: 0,
      appOpensCount: 0,
      signupsCount: 0,
      quizzesPlayedCount: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(docRef, finalData);
    return finalData;
  },

  /**
   * Update an existing campaign
   */
  updateCampaign: async (id, updates) => {
    if (!id) throw new Error('Campaign ID is required');
    const docRef = ref(COLLECTIONS.referralCampaigns, id);
    const updateData = {
      ...updates,
      updatedAt: serverTimestamp(),
    };
    await updateDoc(docRef, updateData);
    return { id, ...updateData };
  },

  /**
   * Delete a campaign
   */
  deleteCampaign: async (id) => {
    if (!id) throw new Error('Campaign ID is required');
    await deleteDoc(ref(COLLECTIONS.referralCampaigns, id));
    return true;
  },

  /**
   * Toggle campaign status (active <-> paused)
   */
  toggleStatus: async (id, currentStatus) => {
    const nextStatus = currentStatus === 'active' ? 'paused' : 'active';
    await referralService.updateCampaign(id, { status: nextStatus });
    return nextStatus;
  },

  /**
   * Real-time subscription to marketing campaigns
   */
  subscribeCampaigns: (callback, limitCount = 100) => {
    const q = query(
      col(COLLECTIONS.referralCampaigns),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    return onSnapshot(q, (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(items);
    }, (err) => {
      console.warn('subscribeCampaigns index error, falling back:', err);
      getDocs(col(COLLECTIONS.referralCampaigns)).then((snap) => {
        const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        callback(items);
      }).catch(e => console.error('Fallback campaigns fetch error:', e));
    });
  },

  /**
   * Get single campaign by code
   */
  getCampaignByCode: async (code) => {
    if (!code) return null;
    const cleanCode = code.toLowerCase().trim();
    try {
      const direct = await getDoc(ref(COLLECTIONS.referralCampaigns, cleanCode));
      if (direct.exists()) {
        return { id: direct.id, ...direct.data() };
      }
      const q = query(col(COLLECTIONS.referralCampaigns), where('code', '==', cleanCode), limit(1));
      const snap = await getDocs(q);
      if (!snap.empty) {
        return { id: snap.docs[0].id, ...snap.docs[0].data() };
      }
    } catch (err) {
      console.error('Error fetching campaign by code:', err);
    }
    return null;
  },

  /**
   * Log telemetry event (click, app_open, signup, quiz_completed)
   * and update campaign atomic counts.
   */
  logEvent: async (eventData) => {
    const {
      code,
      type = 'click',
      platform = 'web',
      userId = null,
      userEmail = null,
      userName = null,
      targetDestination = null,
    } = eventData;

    if (!code) return;
    const cleanCode = code.toLowerCase().trim();

    try {
      // 1. Add event log entry
      await addDoc(col(COLLECTIONS.referralEvents), {
        code: cleanCode,
        type,
        platform,
        userId: userId || null,
        userEmail: userEmail || null,
        userName: userName || null,
        targetDestination: targetDestination || null,
        createdAt: serverTimestamp(),
      });

      // 2. Increment campaign atomic metrics if this is a campaign code
      const campaignRef = ref(COLLECTIONS.referralCampaigns, cleanCode);
      const campSnap = await getDoc(campaignRef);
      if (campSnap.exists()) {
        const increments = {};
        if (type === 'click') increments.clicksCount = increment(1);
        if (type === 'app_open') increments.appOpensCount = increment(1);
        if (type === 'signup') increments.signupsCount = increment(1);
        if (type === 'quiz_completed') increments.quizzesPlayedCount = increment(1);

        increments.updatedAt = serverTimestamp();
        await updateDoc(campaignRef, increments);
      }

      // 3. If user signed up with a referrer user code, increment referrer user stats
      if (type === 'signup' && userId) {
        const userQ = query(collection(db, 'users'), where('referralCode', '==', cleanCode.toUpperCase()), limit(1));
        const userSnap = await getDocs(userQ);
        if (!userSnap.empty) {
          const referrerDoc = userSnap.docs[0];
          await updateDoc(referrerDoc.ref, {
            totalReferralsCount: increment(1),
          });
        }
      }
    } catch (err) {
      console.error('Failed to log referral event:', err);
    }
  },

  /**
   * Real-time subscription to recent referral events stream (limit: 50)
   */
  subscribeRecentEvents: (callback, limitCount = 50) => {
    const q = query(
      col(COLLECTIONS.referralEvents),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    return onSnapshot(q, (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(items);
    }, (err) => {
      console.warn('subscribeRecentEvents error:', err);
      callback([]);
    });
  },

  /**
   * Fetch top user advocates (users who referred the most contestants)
   */
  getTopUserReferrers: async (limitCount = 20) => {
    try {
      const q = query(
        collection(db, 'users'),
        where('totalReferralsCount', '>', 0),
        orderBy('totalReferralsCount', 'desc'),
        limit(limitCount)
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (err) {
      console.warn('getTopUserReferrers error:', err);
      return [];
    }
  }
};

// ─── Player Game Play & Live Trivia Service (Web & iOS) ─────────────────────
export const gamePlayService = {
  /**
   * Fetch active & completed seasons open for contestant play on Web.
   * Only returns seasons where 'allowed_devices' explicitly includes 'web'.
   */
  getActiveSeasons: async () => {
    try {
      let seasons = [];

      // 1. Attempt targeted query for seasons explicitly configured for Web play
      try {
        const qWeb = query(
          col(COLLECTIONS.seasons),
          where('allowed_devices', 'array-contains', 'web')
        );
        const snapWeb = await getDocs(qWeb);
        seasons = snapWeb.docs.map((d) => ({
          id: d.id,
          season_id: d.data().season_id || d.id,
          ...d.data(),
        }));
      } catch (queryErr) {
        console.warn('Direct allowed_devices query failed, falling back to status query:', queryErr);
        const qFallback = query(
          col(COLLECTIONS.seasons),
          where('status', 'in', ['active', 'completed'])
        );
        const snapFallback = await getDocs(qFallback);
        seasons = snapFallback.docs.map((d) => ({
          id: d.id,
          season_id: d.data().season_id || d.id,
          ...d.data(),
        }));
      }

      // 2. Filter strictly for web seasons that are active or completed
      return seasons.filter((s) => {
        const status = (s.status || '').toLowerCase();
        if (status !== 'active' && status !== 'completed') return false;

        const raw = s.allowed_devices || s.allowedDevices;
        const allowed = Array.isArray(raw)
          ? raw.map((d) => String(d).toLowerCase())
          : typeof raw === 'string'
          ? [raw.toLowerCase()]
          : [];

        // Must explicitly allow 'web'. Mobile-only seasons (no web) are excluded.
        return allowed.includes('web');
      });
    } catch (err) {
      console.error('getActiveSeasons error:', err);
      return [];
    }
  },

  /**
   * Get single season details
   */
  getSeasonDetails: async (seasonId) => {
    if (!seasonId) return null;
    const snap = await getDoc(ref(COLLECTIONS.seasons, seasonId));
    return snap.exists() ? { id: snap.id, season_id: snap.data()?.season_id || snap.id, ...snap.data() } : null;
  },

  /**
   * Get recent 10 quizzes/rounds for a season ordered by end date descending
   */
  getQuizzes: async (seasonId, limitCount = 10) => {
    if (!seasonId) return [];
    // 1. Try orderBy('end', 'desc') with limit(limitCount)
    try {
      const q = query(
        col(`${COLLECTIONS.seasons}/${seasonId}/quizzes`),
        orderBy('end', 'desc'),
        limit(limitCount)
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        return snap.docs.map((d) => ({
          id: d.id,
          quiz_id: d.data().quiz_id || d.id,
          ...d.data(),
        }));
      }
    } catch (err) {
      console.warn('getQuizzes orderBy("end", "desc") index note:', err);
    }

    // 2. Try orderBy('endDate', 'desc') with limit(limitCount)
    try {
      const q = query(
        col(`${COLLECTIONS.seasons}/${seasonId}/quizzes`),
        orderBy('endDate', 'desc'),
        limit(limitCount)
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        return snap.docs.map((d) => ({
          id: d.id,
          quiz_id: d.data().quiz_id || d.id,
          ...d.data(),
        }));
      }
    } catch (err) {
      console.warn('getQuizzes orderBy("endDate", "desc") note:', err);
    }

    // 3. Robust fallback: fetch docs and sort by end date descending locally, returning top 10
    try {
      const snap = await getDocs(col(`${COLLECTIONS.seasons}/${seasonId}/quizzes`));
      const list = snap.docs.map((d) => ({
        id: d.id,
        quiz_id: d.data().quiz_id || d.id,
        ...d.data(),
      }));

      const getMillis = (val) => {
        if (!val) return 0;
        if (typeof val.toMillis === 'function') return val.toMillis();
        if (typeof val.toDate === 'function') return val.toDate().getTime();
        if (val.seconds) return val.seconds * 1000;
        const parsed = new Date(val).getTime();
        return isNaN(parsed) ? 0 : parsed;
      };

      list.sort((a, b) => {
        const timeA = getMillis(a.end || a.endDate || a.end_date || a.start || a.startDate);
        const timeB = getMillis(b.end || b.endDate || b.end_date || b.start || b.startDate);
        return timeB - timeA;
      });

      return list.slice(0, limitCount);
    } catch (err) {
      console.error('getQuizzes fallback query error:', err);
      return [];
    }
  },

  /**
   * Get single quiz details
   */
  getQuizDetails: async (seasonId, quizId) => {
    if (!seasonId || !quizId) return null;
    const snap = await getDoc(ref(`${COLLECTIONS.seasons}/${seasonId}/quizzes`, quizId));
    return snap.exists() ? { id: snap.id, quiz_id: snap.data()?.quiz_id || snap.id, ...snap.data() } : null;
  },

  /**
   * Get all questions for a specific quiz
   */
  getQuestions: async (seasonId, quizId) => {
    if (!seasonId || !quizId) return [];
    const snap = await getDocs(col(`${COLLECTIONS.seasons}/${seasonId}/quizzes/${quizId}/questions`));
    return snap.docs.map((d) => ({
      id: d.id,
      question_id: d.data().question_id || d.id,
      ...d.data(),
    }));
  },

  /**
   * Check if user has already played this quiz
   */
  getUserResult: async (seasonId, quizId, userId) => {
    if (!seasonId || !quizId || !userId) return null;
    const snap = await getDoc(ref(`${COLLECTIONS.seasons}/${seasonId}/quizzes/${quizId}/results`, userId));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  },

  /**
   * Submit quiz results after completing game
   */
  submitQuizResult: async ({
    seasonId,
    quizId,
    quizTitle,
    user,
    score,
    timeTaken,
    numQuestions,
    userResponses,
  }) => {
    if (!seasonId || !quizId || !user?.uid) return null;
    const userId = user.uid;
    const userName = user.displayName || user.email?.split('@')[0] || 'Contestant';
    const photoURL = user.photoURL || '';

    // Cache locally in gamesPlayed
    try {
      const stored = JSON.parse(localStorage.getItem('gamesPlayed') || '[]');
      if (!stored.includes(quizId)) {
        stored.push(quizId);
        localStorage.setItem('gamesPlayed', JSON.stringify(stored));
      }
    } catch {}

    const resultPayload = {
      season_id: seasonId,
      quiz_id: quizId,
      title: quizTitle || 'Bible Contest Quiz',
      userId,
      userName,
      img: photoURL,
      score: Number(score) || 0,
      timeTaken: Number(timeTaken) || 0,
      numQuestions: Number(numQuestions) || 0,
      userResponses: userResponses || {},
      timestamp: serverTimestamp(),
      submittedAt: new Date().toISOString(),
      platform: 'web',
    };

    await setDoc(ref(`${COLLECTIONS.seasons}/${seasonId}/quizzes/${quizId}/results`, userId), resultPayload);

    // Compute calendar week & month keys matching Flutter mobile app
    const now = new Date();
    const getWeekOfYear = (d) => {
      const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
      const dayNum = date.getUTCDay() || 7;
      date.setUTCDate(date.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
      return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
    };
    const weekId = `week_${now.getFullYear()}_${getWeekOfYear(now)}`;
    const monthId = `month_${now.getFullYear()}_${now.getMonth() + 1}`;

    const scoreNum = Number(score) || 0;
    const timeNum = Number(timeTaken) || 0;

    const standingsPayload = {
      userId,
      userName: userName || 'Contestant',
      img: photoURL || '',
      score: increment(scoreNum),
      timeTaken: increment(timeNum),
      gamesCount: increment(1),
      lastUpdated: serverTimestamp(),
    };

    // Update standings collections matching mobile Flutter app DB.dart
    try {
      await Promise.allSettled([
        setDoc(ref(`standings_weekly/${weekId}/users`, userId), standingsPayload, { merge: true }),
        setDoc(ref(`standings_monthly/${monthId}/users`, userId), standingsPayload, { merge: true }),
        setDoc(ref(`standings_season/${seasonId}/users`, userId), standingsPayload, { merge: true }),
        setDoc(ref(`standings_season_weekly/${seasonId}_${weekId}/users`, userId), standingsPayload, { merge: true }),
        setDoc(ref(`standings_season_monthly/${seasonId}_${monthId}/users`, userId), standingsPayload, { merge: true }),
        setDoc(ref(`seasons/${seasonId}/members`, userId), {
          no_of_games_played: increment(1),
          total_score: increment(scoreNum),
          time_spent: increment(timeNum),
          lastActive: serverTimestamp(),
          userName: userName || 'Contestant',
          img: photoURL || '',
        }, { merge: true }),
        setDoc(ref('users', userId), {
          userId,
          userName: userName || 'Contestant',
          emailId: user.email || '',
          email: user.email || '',
          img: photoURL || '',
          photoURL: photoURL || '',
          totalScore: increment(scoreNum),
          total_points_scored: increment(scoreNum),
          points: increment(scoreNum),
          quizzesPlayed: increment(1),
          total_quizzes_played: increment(1),
          total_time_spent: increment(timeNum),
          lastPlayedAt: serverTimestamp(),
        }, { merge: true }),
      ]);

      if (userProfileCache[userId]) {
        delete userProfileCache[userId];
      }
    } catch (e) {
      console.warn('Leaderboard standings write note:', e);
    }

    return resultPayload;
  },

  /**
   * Get partitioned quizzes matching Flutter SeasonHome logic:
   *  - Ongoing (sorted by end asc)
   *  - Upcoming (sorted by start asc)
   *  - Finished (sorted by end desc)
   */
  getPartitionedQuizzes: async (seasonId, limitCount = 20) => {
    if (!seasonId) return { ongoing: [], upcoming: [], finished: [], all: [] };
    try {
      const snap = await getDocs(col(`${COLLECTIONS.seasons}/${seasonId}/quizzes`));
      const allDocs = snap.docs.map((d) => ({
        id: d.id,
        quiz_id: d.data().quiz_id || d.id,
        ...d.data(),
      }));

      const getMillis = (val) => {
        if (!val) return 0;
        if (typeof val.toMillis === 'function') return val.toMillis();
        if (typeof val.toDate === 'function') return val.toDate().getTime();
        if (val.seconds) return val.seconds * 1000;
        const parsed = new Date(val).getTime();
        return isNaN(parsed) ? 0 : parsed;
      };

      const ongoing = allDocs
        .filter((q) => (q.status || '').toLowerCase() === 'ongoing')
        .sort((a, b) => getMillis(a.end || a.endDate) - getMillis(b.end || b.endDate));

      const upcoming = allDocs
        .filter((q) => (q.status || '').toLowerCase() === 'upcoming')
        .sort((a, b) => getMillis(a.start || a.startDate) - getMillis(b.start || b.startDate));

      const finished = allDocs
        .filter((q) => (q.status || '').toLowerCase() === 'finished')
        .sort((a, b) => getMillis(b.end || b.endDate) - getMillis(a.end || a.endDate));

      const all = [...allDocs].sort((a, b) => getMillis(b.end || b.endDate) - getMillis(a.end || a.endDate));

      return {
        ongoing: ongoing.slice(0, limitCount),
        upcoming: upcoming.slice(0, limitCount),
        finished: finished.slice(0, limitCount),
        all: all.slice(0, limitCount),
      };
    } catch (err) {
      console.error('getPartitionedQuizzes error:', err);
      return { ongoing: [], upcoming: [], finished: [], all: [] };
    }
  },

  /**
   * Get season leaderboard settings from Firestore
   */
  getSeasonLeaderboardSetting: async (seasonId) => {
    if (!seasonId) return null;
    try {
      const snap = await getDoc(ref('season_leaderbaord_setting', seasonId));
      if (snap.exists()) {
        const d = snap.data();
        return {
          view: d.view,
          appearance: d.appearance,
          season_leaderboard_available: d.season_leaderboard_available !== false,
        };
      }
    } catch (e) {
      console.warn('getSeasonLeaderboardSetting note:', e);
    }
    return null;
  },

  /**
   * Check user permission status for private seasons
   */
  getSeasonPermissionStatus: async (seasonId, userId) => {
    if (!seasonId || !userId) return 'approved';
    try {
      const memberDoc = await getDoc(ref(`seasons/${seasonId}/members`, userId));
      if (memberDoc.exists()) {
        const d = memberDoc.data();
        const allowedToPlay = d.allowed_to_play === true;
        const joinStatus = (d.join_request_status || '').toLowerCase();
        if (allowedToPlay && joinStatus === 'approved') return 'approved';
        if (joinStatus === 'pending') return 'pending';
        if (joinStatus === 'rejected') return 'rejected';
        return 'approved';
      }
      return 'none';
    } catch (e) {
      console.warn('getSeasonPermissionStatus note:', e);
      return 'none';
    }
  },

  /**
   * Request access to private season
   */
  requestSeasonAccess: async (seasonId, user) => {
    if (!seasonId || !user?.uid) return;
    await setDoc(ref(`seasons/${seasonId}/members`, user.uid), {
      userId: user.uid,
      userName: user.displayName || 'Guest User',
      emailId: user.email || '',
      img: user.photoURL || '',
      allowed_to_play: false,
      join_request_status: 'pending',
      requestedOn: serverTimestamp(),
      no_of_games_played: increment(0),
      total_score: increment(0),
      time_spent: increment(0),
    }, { merge: true });
  },

  /**
   * Get leaderboard standings for a single quiz matching Flutter LeaderBoardScreen.dart
   */
  getQuizLeaderboard: async (seasonId, quizId, limitCount = 100) => {
    if (!seasonId || !quizId) return [];
    try {
      const q = query(
        col(`${COLLECTIONS.seasons}/${seasonId}/quizzes/${quizId}/results`),
        limit(limitCount)
      );
      const snap = await getDocs(q);
      const docs = snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          userId: data.userId || d.id,
          userName: data.userName || 'Guest User',
          img: data.img || '',
          score: Number(data.score) || 0,
          timeTaken: Number(data.timeTaken) || 0,
          ...data,
        };
      }).filter((u) => !(u.score === 0 && u.timeTaken === 0));

      // Sort: score descending, then timeTaken ascending (faster is better tie-breaker)
      docs.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return a.timeTaken - b.timeTaken;
      });

      // Compute rank with tie handling matching Flutter
      let rank = 0;
      let prevScore = -1;
      let prevTimeTaken = -1;
      return docs.map((user) => {
        if (!(user.score === prevScore && user.timeTaken === prevTimeTaken)) {
          rank++;
        }
        prevScore = user.score;
        prevTimeTaken = user.timeTaken;
        return {
          ...user,
          rank,
        };
      });
    } catch (err) {
      console.error('getQuizLeaderboard error:', err);
      return [];
    }
  },

  /**
   * Get aggregated leaderboard for an entire season matching Flutter SeasonLeaderboardScreen.dart
   * Tabs: 'weekly' | 'monthly' | 'overall'
   */
  getSeasonLeaderboard: async (seasonId, timeframe = 'weekly') => {
    if (!seasonId) return [];
    const now = new Date();
    const getWeekOfYear = (d) => {
      const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
      const dayNum = date.getUTCDay() || 7;
      date.setUTCDate(date.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
      return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
    };
    const weekId = `week_${now.getFullYear()}_${getWeekOfYear(now)}`;
    const monthId = `month_${now.getFullYear()}_${now.getMonth() + 1}`;

    let standingsPath;
    if (timeframe === 'weekly') {
      standingsPath = `standings_season_weekly/${seasonId}_${weekId}/users`;
    } else if (timeframe === 'monthly') {
      standingsPath = `standings_season_monthly/${seasonId}_${monthId}/users`;
    } else {
      standingsPath = `standings_season/${seasonId}/users`;
    }

    try {
      const q = query(col(standingsPath), orderBy('score', 'desc'), limit(100));
      const snap = await getDocs(q);
      if (!snap.empty) {
        let users = snap.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            userId: docSnap.id,
            userName: data.userName || 'Guest User',
            img: data.img || '',
            score: Number(data.score) || 0,
            timeTaken: Number(data.timeTaken) || 0,
            games: Number(data.gamesCount) || 1,
          };
        });

        // Sort: score desc, then timeTaken asc
        users.sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          return a.timeTaken - b.timeTaken;
        });

        // Assign rank with tie handling matching Flutter
        let rank = 0;
        let prevScore = -1;
        let prevTimeTaken = -1;
        return users.map((u) => {
          if (!(u.score === prevScore && u.timeTaken === prevTimeTaken)) {
            rank++;
          }
          prevScore = u.score;
          prevTimeTaken = u.timeTaken;
          return {
            ...u,
            rank,
          };
        });
      }
    } catch (err) {
      console.warn(`standings query note for ${standingsPath}:`, err);
    }

    // Fallback: Check seasons/{seasonId}/members
    try {
      const snap = await getDocs(col(`seasons/${seasonId}/members`));
      if (!snap.empty) {
        let users = snap.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            userId: docSnap.id,
            userName: data.userName || data.name || data.displayName || 'Guest User',
            img: data.img || data.photoURL || '',
            score: Number(data.total_score || data.score || 0),
            timeTaken: Number(data.time_spent || data.timeTaken || 0),
            games: Number(data.no_of_games_played || data.gamesCount || 1),
          };
        }).filter((u) => u.score > 0 || u.games > 0);

        users.sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          return a.timeTaken - b.timeTaken;
        });

        let rank = 0;
        let prevScore = -1;
        let prevTimeTaken = -1;
        return users.map((u) => {
          if (!(u.score === prevScore && u.timeTaken === prevTimeTaken)) {
            rank++;
          }
          prevScore = u.score;
          prevTimeTaken = u.timeTaken;
          return {
            ...u,
            rank,
          };
        });
      }
    } catch (e) {
      console.warn('seasons members fallback note:', e);
    }

    return [];
  },

  /**
   * Get global leaderboard standings across all contests
   * Supports:
   *  - metric: 'points' | 'streak'
   *  - timeframe: 'currentWeek' | 'pastWeek' | 'currentMonth' | 'pastMonth'
   */
  getGlobalLeaderboard: async (options = {}) => {
    // Handle both object argument or direct string timeframe for backwards compatibility
    const metric = typeof options === 'object' && options.metric ? options.metric : 'points';
    const timeframe = typeof options === 'string' ? options : (options.timeframe || 'currentWeek');

    const now = new Date();
    const getWeekOfYear = (date) => {
      const d = new Date(date);
      const dayOfWeek = (d.getDay() + 6) % 7; // Monday=0, Sunday=6
      const thursday = new Date(d);
      thursday.setDate(d.getDate() + (3 - dayOfWeek));
      const firstDayOfYear = new Date(thursday.getFullYear(), 0, 1);
      const diffDays = Math.floor((thursday - firstDayOfYear) / (24 * 60 * 60 * 1000));
      return Math.floor(diffDays / 7) + 1;
    };

    if (metric === 'streak') {
      try {
        const snap = await getDocs(query(col('users'), limit(250)));
        const users = snap.docs.map((d) => {
          const data = d.data();
          return {
            userId: d.id,
            userName: data.userName || data.name || data.displayName || 'Contestant',
            img: data.img || data.photoURL || '',
            score: Number(data.current_streak) || 0,
            games: Number(data.highest_streak) || 0,
            metric: 'streak',
          };
        }).filter((u) => u.score > 0 || u.games > 0);

        users.sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          return b.games - a.games;
        });

        let rank = 0;
        let prevScore = -1;
        let prevGames = -1;
        return users.slice(0, 100).map((user) => {
          if (!(user.score === prevScore && user.games === prevGames)) {
            rank++;
          }
          prevScore = user.score;
          prevGames = user.games;
          return {
            ...user,
            rank,
          };
        });
      } catch (err) {
        console.error('getGlobalLeaderboard streak error:', err);
        return [];
      }
    }

    // Points Leaderboard: currentWeek, pastWeek, currentMonth, pastMonth
    let collectionPath;
    let docId;

    switch (timeframe) {
      case 'pastWeek': {
        const pastWeekDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        collectionPath = 'standings_weekly';
        docId = `week_${pastWeekDate.getFullYear()}_${getWeekOfYear(pastWeekDate)}`;
        break;
      }
      case 'currentMonth': {
        collectionPath = 'standings_monthly';
        docId = `month_${now.getFullYear()}_${now.getMonth() + 1}`;
        break;
      }
      case 'pastMonth': {
        let prevMonth = now.getMonth(); // 0-based for prev month
        let prevYear = now.getFullYear();
        if (prevMonth === 0) {
          prevMonth = 12;
          prevYear -= 1;
        }
        collectionPath = 'standings_monthly';
        docId = `month_${prevYear}_${prevMonth}`;
        break;
      }
      case 'currentWeek':
      default: {
        collectionPath = 'standings_weekly';
        docId = `week_${now.getFullYear()}_${getWeekOfYear(now)}`;
        break;
      }
    }

    try {
      const snap = await getDocs(query(col(`${collectionPath}/${docId}/users`), orderBy('score', 'desc'), limit(100)));
      if (!snap.empty) {
        const users = snap.docs.map((d) => {
          const data = d.data();
          return {
            userId: d.id,
            userName: data.userName || 'Contestant',
            img: data.img || '',
            score: Number(data.score) || 0,
            timeTaken: Number(data.timeTaken) || 0,
            games: Number(data.gamesCount) || 1,
            metric: 'points',
          };
        });

        users.sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          return a.timeTaken - b.timeTaken;
        });

        let rank = 0;
        let prevScore = -1;
        let prevTime = -1;
        return users.map((user) => {
          if (!(user.score === prevScore && user.timeTaken === prevTime)) {
            rank++;
          }
          prevScore = user.score;
          prevTime = user.timeTaken;
          return {
            ...user,
            rank,
          };
        });
      }
    } catch (e) {
      console.warn(`Points leaderboard query ${collectionPath}/${docId} note:`, e);
    }

    // Fallback: If the selected bucket has no entries yet, fallback to users collection
    try {
      const snap = await getDocs(query(col('users'), limit(100)));
      if (!snap.empty) {
        const users = snap.docs.map((d) => {
          const u = d.data();
          return {
            userId: d.id,
            userName: u.userName || u.name || u.displayName || 'Contestant',
            img: u.img || u.photoURL || '',
            score: Number(u.total_points_scored || u.totalScore || u.score || 0),
            timeTaken: Number(u.total_time_spent || u.timeTaken || 0),
            games: Number(u.total_quizzes_played || u.quizzesPlayed || 1),
            metric: 'points',
          };
        }).filter((u) => u.score > 0);

        users.sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          return a.timeTaken - b.timeTaken;
        });

        let rank = 0;
        let prevScore = -1;
        let prevTime = -1;
        return users.slice(0, 100).map((user) => {
          if (!(user.score === prevScore && user.timeTaken === prevTime)) {
            rank++;
          }
          prevScore = user.score;
          prevTime = user.timeTaken;
          return {
            ...user,
            rank,
          };
        });
      }
    } catch (e) {
      console.warn('Fallback users query error:', e);
    }

    return [];
  },
};





